/**
 * Fleet & Transport API client — Session 3.14 (Section 10).
 *
 * Mirrors the public surfaces exported by
 * apps/api/src/services/fleet/fleetService.ts. Tier-gated routes
 * (Plus+: journeys / tachograph / siteworks / fors) throw an
 * ApiError with code 'TIER_REQUIRED' when called from an Essentials
 * tenant — callers should treat that as a non-fatal "no data" state.
 */

import { apiFetch, ApiError, apiUrl } from './api';
import type { PublishAudience } from '../components/PublishAudienceDialog';
import { getAccessToken } from './auth';
// 2026-06-11 — walkaround photo upload must work on a COLD provisioned
// tablet (no user JWT): fall back to the device token, mirroring apiFetch.
import { getDriverTabletToken } from './driverTabletDeviceAuth';

/* ─────────────────────────── Public types ─────────────────────────── */

/**
 * Refrigeration configurations, in the order a form offers them.
 *
 * Reagan, 2026-09-07: *"a temperature controlled vehical so its marked as that
 * so this could be a van or hgv"* — ORTHOGONAL to vehicleClass, which is why
 * it is its own field rather than a fifth class.
 *
 * Mirrors TEMPERATURE_CONTROLS in the API's fleetService and the CHECK in
 * migration 20260907172641. Three copies of one vocabulary is two too many,
 * but the client cannot import from the API — so this comment is the link, and
 * a route test asserts the server's list matches.
 */
export const TEMPERATURE_CONTROLS = ['none', 'chilled', 'frozen', 'dual'] as const;
export type TemperatureControl = (typeof TEMPERATURE_CONTROLS)[number];

/** What each configuration is called on screen. */
export const TEMPERATURE_CONTROL_LABELS: Record<TemperatureControl, string> = {
  none: 'Not refrigerated',
  chilled: 'Chilled',
  frozen: 'Frozen',
  dual: 'Dual (chilled + frozen)',
};

/** The configurations that actually hold a load at temperature — the ones an
 *  ATP certificate belongs to. Derived, so adding one cannot leave this
 *  behind. */
export const REFRIGERATED_CONTROLS: readonly TemperatureControl[] =
  TEMPERATURE_CONTROLS.filter((c) => c !== 'none');

export function isRefrigerated(v: string | null | undefined): boolean {
  return v != null && (REFRIGERATED_CONTROLS as readonly string[]).includes(v);
}

export interface VehiclePublic {
  id: string;
  tenantId: string;
  registration: string;
  make: string;
  model: string;
  year: number | null;
  vehicleType: string;
  fuelType: string | null;
  vehicleClass: string | null;
  hoursRegulated: boolean;
  assignedDriverId: string | null;
  emissionsStandard: string | null;
  cazCompliant: boolean;
  /**
   * none | chilled | frozen | dual, or NULL for "nobody has recorded it".
   *
   * NULL is NOT 'none'. A planner picking a vehicle for a chilled load has to
   * tell "we know it is not refrigerated" from "we have never been told" — the
   * same distinction tabletFitted makes below.
   */
  temperatureControl: string | null;
  /** ATP certificate expiry. Only ever set on a refrigerated vehicle; the
   *  database refuses it otherwise. */
  atpExpiry: string | null;
  motExpiry: string | null;
  roadTaxExpiry: string | null;
  /** What the vehicle is insured to: the fleet policy's date when it is on the policy, else its own. */
  insuranceExpiry: string | null;
  /** The vehicle's own date, which Edit Vehicle changes. */
  ownInsuranceExpiry: string | null;
  /** Insured on the company fleet policy. */
  onFleetPolicy: boolean;
  /** A fleet policy schedule is recorded and does not name this vehicle. */
  notOnFleetSchedule: boolean;
  oLicenceNumber: string | null;
  oLicenceExpiry: string | null;
  /** FLT-VR-003 (RFC-009) — operator-nominated PMI interval, 1-13 weeks
   *  per the DVSA Guide to Maintaining Roadworthiness. 6 weeks minimum
   *  for vehicles 12+ years old. Null for non-HGV vehicles. */
  pmiIntervalWeeks: number | null;
  /** General service recurrence in MONTHS; null = one-off, no repeat. NOT the
   *  PMI interval above, which is weeks and HGV-only — different obligation. */
  serviceIntervalMonths: number | null;
  /** Date of the most-recent laden brake test (or signed-off
   *  decelerometer assessment with risk note). Null if never tested. */
  lastBrakeTestDate: string | null;
  /** 'laden_rbt' | 'ebpms' | 'decelerometer'. From April 2025 laden RBT
   *  and EBPMS are the only accepted methods (with exceptions). */
  lastBrakeTestMethod: string | null;
  /** Declared fitted by an operator. NULL = not recorded, NOT "no tablet". */
  tabletFitted: boolean | null;
  tabletLastPing: string | null;
  immobiliserStatus: string;
  lastWalkaround: string | null;
  /** The ANCHOR a human set — still exactly the date typed into the form. */
  nextInspectionDue: string | null;
  /** THE DATE TO SHOW: first occurrence on or after today, derived server-side
   *  from the anchor and the interval. Equals `nextInspectionDue` when there is
   *  no interval or the anchor is still in the future. Use this for badges and
   *  compliance counts; use the anchor only when editing. */
  serviceDueNext: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Open defects against this vehicle (VR-003). `null` means the response did
   * not count them — NOT that there are none. Only the list route counts.
   */
  openDefectCount: number | null;
  /** Booked in for its MOT on this date (today or later), from the list route only. */
  motBookingDate?: string | null;
}

export interface VehicleStats {
  total: number;
  compliant: number;
  expiringSoon: number;
  expired: number;
  companyCount: number;
  greyFleetCount: number;
  /**
   * VR-003 — defect ROWS open across the fleet, and the number of vehicles
   * they sit on. The register used to show the count of QUARANTINED vehicles
   * under an "Open defects" label, which missed every defect not severe enough
   * to immobilise a vehicle — including `minor`, the defect form's default.
   */
  openDefects: number;
  vehiclesWithOpenDefects: number;
}

export interface VehicleListResult {
  vehicles: VehiclePublic[];
  stats: VehicleStats;
  /** Registrations on the fleet insurance schedule with no vehicle on the register. */
  notOnRegister: string[];
}

/** What DVLA holds against a plate (GET /api/fleet/vehicle-enquiry/:registration). */
export interface DvlaVehicleRecord {
  registration: string;
  make: string | null;
  colour: string | null;
  yearOfManufacture: number | null;
  motExpiry: string | null;
  taxDue: string | null;
  typeApproval: string | null;
  vehicleClass: 'car' | 'van' | 'hgv' | null;
  fleetFuelType: 'petrol' | 'diesel' | 'hybrid' | 'electric' | null;
}

export interface DriverPublic {
  id: string;
  tenantId: string;
  employeeId: string | null;
  employeeName: string | null;
  // Display last-4 only. The full licence is sensitive PII held
  // encrypted on the employee (audited reveal in HR Sensitive Info).
  licenceLast4: string | null;
  licenceCategories: string[];
  licenceExpiry: string | null;
  penaltyPoints: number;
  dvlaCheckDate: string | null;
  dvlaCheckDue: string | null;
  driverType: string;
  d4MedicalExpiry: string | null;
  cpcCardExpiry: string | null;
  tachographCardNumber: string | null;
  tachographCardExpiry: string | null;
  greyFleet: boolean;
  businessInsuranceConfirmed: boolean;
  annualVehicleCheckDate: string | null;
  notes: string | null;
  licenceExpired: boolean;
  d4Expired: boolean;
  tachographCardExpired: boolean;
  /**
   * THE DRIVING RESTRICTION, AS THE SERVER ANSWERS IT (2026-09-05, TODO 089).
   *
   * Read these. Do NOT rebuild the rule from the dates above — that is what the
   * Driver Register used to do, and it made a second implementation that agreed
   * with the server only by coincidence.
   */
  drivingBlocked: boolean;
  drivingBlockReasons: DriverGateReason[];
  /** Inside the four-week ramp, not yet expired. Empty means nothing is close. */
  drivingWarnings: DriverGateWarning[];
  createdAt: string;
  updatedAt: string;
}

/* ── The driving restriction, answered by the server (2026-09-05, TODO 089) ── */

export type DriverGateReason =
  | 'licence_expired'
  | 'dvla_check_overdue'
  | 'toolbox_talk_overdue'
  | 'd4_medical_expired'
  | 'cpc_expired'
  | 'tachograph_card_expired';

export interface DriverGateWarning {
  reason: DriverGateReason;
  /** The Compliance Gate ramp: L4 four weeks out, down to L1 in the final week. */
  level: 1 | 2 | 3 | 4;
  daysRemaining: number;
}

export interface DriverDrivingGate {
  driverId: string;
  vehicleId: string | null;
  /** Resolved server-side from the vehicle register, never sent by the client. */
  vehicleClass: 'hgv' | 'lgv' | 'van' | 'car' | null;
  blocked: boolean;
  reasons: DriverGateReason[];
  warnings: DriverGateWarning[];
  /**
   * Every requirement the server CONSIDERED for this driver in this vehicle.
   * Render one row per entry; a row passes when it is here and in neither
   * `reasons` nor `warnings`. Do not decide this list yourself — which
   * requirements apply depends on the vehicle, and that is the server's answer.
   */
  applicable: DriverGateReason[];
}

/**
 * Ask whether this driver may drive THIS vehicle.
 *
 * `vehicleId` is optional; without it the answer is the general one for the
 * driver, which is what the Driver Register shows. With it, the server resolves
 * the vehicle's class off the register and applies the Group 2 requirements
 * only when the plate is an HGV.
 */
/** One HGV driver on Site Manager. The server decides every status and the verdict. */
export type PreDepartureStatus = 'green' | 'amber' | 'red' | 'grey';
export interface PreDepartureDriver {
  driverId: string;
  name: string | null;
  verdict: Exclude<PreDepartureStatus, 'grey'>;
  fit: { status: PreDepartureStatus; reasons: DriverGateReason[]; warnings: DriverGateWarning[] };
  hours: {
    status: PreDepartureStatus;
    state: 'no_history' | 'rested' | 'rest_short' | 'on_shift' | 'short_before_shift' | 'shift_left_open';
    restSeconds: number | null;
    minimumSeconds: number;
  };
  walkaround: {
    status: PreDepartureStatus;
    state: 'not_done' | 'passed' | 'advisory' | 'defects' | 'critical';
    vehicleReg: string | null;
    completedAtIso: string | null;
  };
}

export async function fetchPreDepartureChecks(signal?: AbortSignal): Promise<PreDepartureDriver[]> {
  return apiFetch<PreDepartureDriver[]>('/api/fleet/site-manager/pre-departure', { method: 'GET', signal });
}

export async function fetchDriverDrivingGate(
  driverId: string,
  vehicleId?: string | null,
  signal?: AbortSignal,
): Promise<DriverDrivingGate> {
  const qs = vehicleId ? `?vehicleId=${encodeURIComponent(vehicleId)}` : '';
  return apiFetch<DriverDrivingGate>(
    `/api/fleet/drivers/${encodeURIComponent(driverId)}/driving-gate${qs}`,
    { method: 'GET', signal },
  );
}

export interface JourneyPublic {
  id: string;
  tenantId: string;
  driverId: string;
  driverName: string | null;
  vehicleId: string;
  vehicleRegistration: string | null;
  journeyDate: string;
  startTime: string | null;
  endTime: string | null;
  distanceMiles: string | null;
  purpose: string | null;
  origin: string | null;
  destination: string | null;
  cazZoneEntered: boolean;
  notes: string | null;
  /**
   * manual = a person entered it. plan_actual = generated when the day
   * completed, from the planner day plus the driver actual tablet times.
   * The log says which: a record derived from a plan is good evidence, but it
   * is not the same as somebody having entered it.
   */
  source?: string;
  /** The stops, in order. Empty on a manual row and on an unplanned day. */
  legs?: JourneyLegPublic[];
  createdAt: string;
  updatedAt: string;
}

/** One stop on a journey. Minutes, because actuals are measured in minutes. */
export interface JourneyLegPublic {
  sequence: number;
  address: string;
  purpose: string;
  driveMinutes: number;
  onSiteMinutes: number;
}

export interface TachographUploadPublic {
  id: string;
  tenantId: string;
  driverId: string;
  driverName: string | null;
  weekEnding: string;
  driverUnitUploaded: boolean;
  vehicleUnitUploaded: boolean;
  analysed: boolean;
  pendingAnalysis: boolean;
  infringementsFound: number;
  driverUnitS3Key: string | null;
  vehicleUnitS3Key: string | null;
  uploadedAt: string;
  uploadedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ToolboxTalkPublic {
  id: string;
  tenantId: string;
  category: string;
  title: string;
  content: string | null;
  keyMessage: string | null;
  icon: string | null;
  topic: string | null;
  /** The talk's filing category: a key on the country's category list. */
  contentCategory: string;
  /** Its name on that list - what a person reads. */
  contentCategoryLabel: string;
  durationLabel: string | null;
  isSystem: boolean;
  revisionNumber: number;
  assignmentCounts: {
    pending: number;
    overdue: number;
    complete: number;
  };
  /**
   * The compliance-gate requirement on this talk - Stage F4, spec-04 section 9.
   *
   * Three states as two flags: Off (not active), Applies (active, not
   * required), Compulsory (both). Prefixed `gate` because the API row also has
   * an `isActive` meaning LIVE rather than withdrawn, and conflating the two is
   * the mistake these names exist to prevent.
   */
  gateIsActive: boolean;
  gateIsRequired: boolean;
  gracePeriodDays: number;
  /** Months between re-deliveries. Null = one-off. */
  renewalMonths: number | null;
  /** Edited since its approval: the new revision waits on the Document Register and cannot be signed. */
  awaitingApproval: boolean;
  /** Its entry on the Document Register. */
  registerDocumentId: string | null;
  /** The revision last published to drivers; published when it equals revisionNumber. */
  publishedRevision: number | null;
  /** Who it was published to: every driver, or the drivers chosen. */
  audienceScope: string;
  audienceEmployeeIds: string[];
  createdAt: string;
  updatedAt: string;
}

/** One immutable revision in a talk's history (newest first). */
export interface ToolboxTalkRevisionPublic {
  id: string;
  revisionNumber: number;
  content: string;
  keyMessage: string | null;
  changeSummary: string | null;
  supersededAt: string | null;
  createdAt: string;
  createdById: string | null;
  isCurrent: boolean;
}

export interface ToolboxTalkInput {
  title: string;
  icon?: string;
  topic?: string;
  /** A key from fetchFleetContentCategories - the server refuses anything else. */
  contentCategory?: string;
  durationLabel?: string;
  content: string;
  keyMessage?: string;
}

export interface ToolboxTalkAssignmentPublic {
  id: string;
  tenantId: string;
  toolboxTalkId: string;
  talkTitle: string | null;
  driverId: string | null;
  driverName: string | null;
  employeeId: string | null;
  assignedAt: string;
  deadline: string | null;
  status: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}


export interface FleetForsRecordPublic {
  id: string;
  tenantId: string;
  oLicenceNumber: string | null;
  oLicenceExpiry: string | null;
  /** RFC-018b — full O Licence details for FORS step 1. */
  transportManagerName: string | null;
  transportManagerCpc: string | null;
  operatingCentreAddress: string | null;
  numberOfVehicles: number | null;
  licenceType: 'standard_national' | 'standard_international' | 'restricted' | null;
  journeyScope: string | null;
  forsLevel: string | null;
  forsMemberNumber: string | null;
  forsExpiry: string | null;
  orgProfile: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleImportRow {
  registration: string;
  make: string;
  model: string;
  year?: string;
  vehicleType?: string;
  motExpiry?: string;
  roadTaxExpiry?: string;
  insuranceExpiry?: string;
  serviceDue?: string;
  notes?: string;
}

export interface VehicleImportPreviewResult {
  preview: VehicleImportRow[];
  errors: Array<{ row: number; field: string; message: string }>;
}

export interface VehicleImportConfirmResult {
  inserted: number;
  updated: number;
  skipped: number;
}

/* ─────────────────────────── Input payloads ─────────────────────────── */

export interface CreateVehiclePayload {
  registration: string;
  make: string;
  model: string;
  year?: number | null;
  vehicleType?: 'company' | 'grey_fleet';
  fuelType?: 'petrol' | 'diesel' | 'hybrid' | 'electric' | null;
  vehicleClass?: 'car' | 'van' | 'hgv' | null;
  /** Sent as null to clear back to "not recorded". */
  temperatureControl?: TemperatureControl | null;
  /**
   * ATP expiry. MUST travel with temperatureControl when the refrigeration is
   * being turned off: the server refuses an ATP date on a non-refrigerated
   * vehicle, so switching chilled → none without clearing this in the same
   * request is a 422.
   */
  atpExpiry?: string | null;
  hoursRegulated?: boolean;
  assignedDriverId?: string | null;
  emissionsStandard?: string | null;
  cazCompliant?: boolean;
  motExpiry?: string | null;
  roadTaxExpiry?: string | null;
  insuranceExpiry?: string | null;
  oLicenceNumber?: string | null;
  oLicenceExpiry?: string | null;
  /** FLT-VR-003 (RFC-009) — HGV maintenance schedule. */
  pmiIntervalWeeks?: number | null;
  serviceIntervalMonths?: number | null;
  lastBrakeTestDate?: string | null;
  lastBrakeTestMethod?: 'laden_rbt' | 'ebpms' | 'decelerometer' | null;
  /** Sent as null to clear it back to "not recorded" — a real answer, not a gap. */
  tabletFitted?: boolean | null;
  immobiliserStatus?: 'active' | 'quarantine';
  lastWalkaround?: string | null;
  nextInspectionDue?: string | null;
  notes?: string | null;
}

export type UpdateVehiclePayload = Partial<CreateVehiclePayload>;

/** What Edit Driver may change. A driver is made in HR; the licence group is worked out from HR. */
export interface UpdateDriverPayload {
  licenceCategories?: string[];
  licenceExpiry?: string | null;
  penaltyPoints?: number;
  dvlaCheckDate?: string | null;
  dvlaCheckDue?: string | null;
  d4MedicalExpiry?: string | null;
  cpcCardExpiry?: string | null;
  tachographCardNumber?: string | null;
  tachographCardExpiry?: string | null;
  greyFleet?: boolean;
  businessInsuranceConfirmed?: boolean;
  annualVehicleCheckDate?: string | null;
  notes?: string | null;
}

/**
 * Human label for a driver in Fleet lists/dropdowns. Always the
 * linked employee's name; falls back to a masked last-4 token for
 * legacy (no-employee) rows. NEVER the full licence number — that is
 * PII and lives encrypted on the employee only.
 */
export function driverLabel(d: {
  employeeName: string | null;
  licenceLast4: string | null;
}): string {
  const name = (d.employeeName ?? '').trim();
  if (name) return name;
  return d.licenceLast4 ? `Driver ••${d.licenceLast4}` : 'Unassigned driver';
}

/* 2026-06-11 — vehicle-class resolver, MOVED here from FleetPage so the
 * van-run flow (light-vehicle picker) shares the register's exact
 * bucketing rule. The explicit vehicleClass is authoritative; LGV counts
 * as van; the make/model keyword guess is only a fallback for legacy
 * rows with no class set, and can only tell van from car — never HGV. */
const VAN_MODEL_KEYWORDS = [
  'transit', 'sprinter', 'crafter', 'caddy', 'transporter', 'vivaro', 'combo',
  'movano', 'master', 'trafic', 'kangoo', 'berlingo', 'partner', 'expert',
  'boxer', 'relay', 'dispatch', 'ducato', 'doblo', 'scudo', 'daily', 'vito',
  'nv200', 'nv300', 'nv400', 'proace', 'jumpy', 'jumper', 'talento',
];
function vehicleIsVan(v: { make: string; model: string }): boolean {
  const s = `${v.make} ${v.model}`.toLowerCase();
  return VAN_MODEL_KEYWORDS.some((k) => s.includes(k));
}
export function resolveVehicleClass(v: {
  make: string;
  model: string;
  vehicleClass: string | null;
}): 'car' | 'van' | 'hgv' {
  if (v.vehicleClass === 'hgv') return 'hgv';
  if (v.vehicleClass === 'van' || v.vehicleClass === 'lgv') return 'van';
  if (v.vehicleClass === 'car') return 'car';
  return vehicleIsVan(v) ? 'van' : 'car';
}

/* ─────────────── Van / car runs (slice 1, 2026-06-11) ─────────────── */

/** The standalone NON-REGULATED light-vehicle run. */
export interface VanRunPublic {
  runId: string;
  clockInId: string;
  driverId: string;
  employeeId: string;
  vehicleId: string;
  vehicleReg: string;
  startedAt: string;
  alreadyClockedIn: boolean;
  resumed: boolean;
  /** Walkaround offered daily; FORCED when the vehicle has no completed
   *  check in the last 7 days. */
  walkaroundForced: boolean;
  lastWalkaroundAt: string | null;
}

export async function startVanRun(input: {
  driverId: string;
  vehicleId: string;
}): Promise<VanRunPublic> {
  return apiFetch<VanRunPublic>('/api/fleet/driver-tablet/van-run/start', {
    method: 'POST',
    body: input,
  });
}

export async function fetchCurrentVanRun(
  driverId: string,
  signal?: AbortSignal,
): Promise<VanRunPublic | null> {
  return apiFetch<VanRunPublic | null>(
    `/api/fleet/driver-tablet/van-run/current?driverId=${encodeURIComponent(driverId)}`,
    { method: 'GET', signal },
  );
}

/** Slice 2 — audit-only record that the daily walkaround offer was
 *  declined (the weekly FORCE is server-side and can't be declined). */
export async function declineVanWalkaround(
  runId: string,
): Promise<{ runId: string }> {
  return apiFetch<{ runId: string }>(
    '/api/fleet/driver-tablet/van-run/decline-walkaround',
    { method: 'POST', body: { runId } },
  );
}

export async function endVanRun(input: {
  runId: string;
  alsoClockOut: boolean;
}): Promise<{ runId: string; endedAt: string; clockedOut: boolean }> {
  return apiFetch<{ runId: string; endedAt: string; clockedOut: boolean }>(
    '/api/fleet/driver-tablet/van-run/end',
    { method: 'POST', body: input },
  );
}

/** VAN-001 item 2 (2026-06-12) — TM/Fleet-Admin van hours report row. */
export interface VanHoursDriverRow {
  driverId: string;
  driverName: string | null;
  runs: number;
  vehicles: string[];
  drivingHrs: number;
  siteHrs: number;
  fuellingHrs: number;
  breakHrs: number;
  clockHrs: number;
  hasOpenRun: boolean;
}

export interface VanHoursWeekReport {
  weekStartDate: string;
  drivers: VanHoursDriverRow[];
}

/** Per driver per week, aggregated from the day-log activity events.
 *  week = any date inside the target week (server snaps to Monday);
 *  omit for the current week. */
export async function fetchVanHoursReport(
  week?: string,
  signal?: AbortSignal,
): Promise<VanHoursWeekReport> {
  const qs = week ? `?week=${encodeURIComponent(week)}` : '';
  return apiFetch<VanHoursWeekReport>(`/api/fleet/van-runs/report${qs}`, {
    method: 'GET',
    signal,
  });
}

export interface CreateJourneyPayload {
  driverId: string;
  vehicleId: string;
  journeyDate: string;
  startTime?: string | null;
  endTime?: string | null;
  distanceMiles?: string | null;
  purpose?: string | null;
  origin?: string | null;
  destination?: string | null;
  cazZoneEntered?: boolean;
  notes?: string | null;
}

export type UpdateJourneyPayload = Partial<CreateJourneyPayload>;

export interface UploadTachographPayload {
  driverId: string;
  weekEnding: string;
  driverUnitUploaded?: boolean;
  vehicleUnitUploaded?: boolean;
  driverUnitS3Key?: string | null;
  vehicleUnitS3Key?: string | null;
}

export interface AssignTransportTalkPayload {
  toolboxTalkId: string;
  employeeIds: string[];
}

export interface ListJourneysQuery {
  driverId?: string;
  vehicleId?: string;
  from?: string;
  to?: string;
}

export interface ListSiteWorksQuery {
  from: string;
  to: string;
}

export interface CreateSiteWorksPayload {
  assignmentDate: string;
  operativeId?: string | null;
  vehicleId?: string | null;
  destination?: string | null;
  cazZone?: boolean;
  startTime?: string | null;
  endTime?: string | null;
  status?: 'assigned' | 'in_progress' | 'complete' | 'cancelled';
  notes?: string | null;
  jobReference?: string | null;
}

export type UpdateSiteWorksPayload = Partial<CreateSiteWorksPayload>;

export interface UpsertForsPayload {
  oLicenceNumber?: string | null;
  oLicenceExpiry?: string | null;
  /** RFC-018b — FORS step 1 fields persisted on the FORS record. */
  transportManagerName?: string | null;
  transportManagerCpc?: string | null;
  operatingCentreAddress?: string | null;
  numberOfVehicles?: number | null;
  licenceType?: 'standard_national' | 'standard_international' | 'restricted' | null;
  journeyScope?: string | null;
  forsLevel?: string | null;
  forsMemberNumber?: string | null;
  forsExpiry?: string | null;
  orgProfile?: Record<string, unknown>;
}

/* ─────────────────────────── Vehicles ─────────────────────────── */

export async function fetchVehicles(signal?: AbortSignal): Promise<VehicleListResult> {
  return apiFetch<VehicleListResult>('/api/fleet/vehicles', { method: 'GET', signal });
}

export async function fetchDvlaConfigured(signal?: AbortSignal): Promise<{ configured: boolean }> {
  return apiFetch<{ configured: boolean }>('/api/fleet/vehicle-enquiry/configured', { method: 'GET', signal });
}

export async function lookupDvlaVehicle(registration: string, signal?: AbortSignal): Promise<DvlaVehicleRecord> {
  return apiFetch<DvlaVehicleRecord>(`/api/fleet/vehicle-enquiry/${encodeURIComponent(registration)}`, {
    method: 'GET',
    signal,
  });
}

export async function fetchVehicle(id: string, signal?: AbortSignal): Promise<{
  vehicle: VehiclePublic;
  inspections: unknown[];
  openDefects: unknown[];
  walkaroundChecks: unknown[];
  motHistory: unknown[];
  repairs: unknown[];
}> {
  return apiFetch(`/api/fleet/vehicles/${encodeURIComponent(id)}`, { method: 'GET', signal });
}

export async function createVehicle(payload: CreateVehiclePayload): Promise<VehiclePublic> {
  return apiFetch<VehiclePublic>('/api/fleet/vehicles', { method: 'POST', body: payload });
}

export async function updateVehicle(
  id: string,
  payload: UpdateVehiclePayload,
): Promise<VehiclePublic> {
  return apiFetch<VehiclePublic>(`/api/fleet/vehicles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

export async function deleteVehicle(id: string): Promise<void> {
  await apiFetch<unknown>(`/api/fleet/vehicles/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ───────────────── Vehicle child records — manual create ─────────────────
 * FLT-VR-005 (RFC-018) — base level of the platform is human-editable.
 * Forms inside the Vehicle Detail card post to these endpoints.
 * --------------------------------------------------------------------- */

export interface CreateVehicleDefectPayload {
  reportedDate: string;
  description: string;
  severity?: 'minor' | 'major' | 'safety_critical';
  status?: 'open' | 'fixed';
  fixedDate?: string | null;
  cost?: number | null;
}
export async function createVehicleDefect(
  vehicleId: string,
  payload: CreateVehicleDefectPayload,
): Promise<unknown> {
  return apiFetch(`/api/fleet/vehicles/${encodeURIComponent(vehicleId)}/defects`, {
    method: 'POST',
    body: payload,
  });
}

// FLT-WA-RETIRE (2026-06-12): createVehicleWalkaround removed — manual
// entries now use recordManualWalkaroundCheck (full checklist, one live
// store). vehicle_walkaround_checks is read-only history.

export interface CreateVehicleMotPayload {
  testDate: string;
  expiryDate: string;
  result?: 'pass' | 'fail' | 'advisory';
  mileage?: number | null;
  advisories?: string;
  failures?: string;
}
export async function createVehicleMotHistory(
  vehicleId: string,
  payload: CreateVehicleMotPayload,
): Promise<unknown> {
  return apiFetch(`/api/fleet/vehicles/${encodeURIComponent(vehicleId)}/mot`, {
    method: 'POST',
    body: payload,
  });
}

export interface CreateVehicleRepairPayload {
  repairDate: string;
  description: string;
  cost?: number | null;
  defectId?: string | null;
  contractorId?: string | null;
}
export async function createVehicleRepair(
  vehicleId: string,
  payload: CreateVehicleRepairPayload,
): Promise<unknown> {
  return apiFetch(`/api/fleet/vehicles/${encodeURIComponent(vehicleId)}/repairs`, {
    method: 'POST',
    body: payload,
  });
}

/* ─────────────────────────── Drivers ─────────────────────────── */

/**
 * 2026-06-03 Driver Tablet T0 — rest-state shape for the Compliance
 * Gate's "Rest period — minimum served" row. See
 * apps/api/src/services/fleet/driverRestStateService.ts.
 */
export interface DriverOpenShiftContextPublic {
  clockedInAt: string;
  /** Resolved Site.name when the open clock-in has a siteId. */
  siteName: string | null;
  /** 'pin' | 'biometric' | 'manual' | 'driver_tablet' | … */
  clockInMethod: string;
  /** False when the open shift began before today (stale / missed
   *  clock-out from a prior day). */
  startedToday: boolean;
}

export interface DriverRestStatePublic {
  driverId: string;
  hasHistory: boolean;
  currentlyClockedIn: boolean;
  /** Open shift details when currentlyClockedIn; null otherwise. */
  openShift: DriverOpenShiftContextPublic | null;
  lastClockedOutAt: string | null;
  /**
   * Rest BEFORE the relevant shift. When currentlyClockedIn, this
   * is the gap between lastClockedOutAt and openShift.clockedInAt.
   * Otherwise, the gap between lastClockedOutAt and now.
   */
  restSecondsServed: number | null;
  minimumServed: boolean;
  minimumThresholdSeconds: number;
  minimumRemainingSeconds: number | null;
}

/**
 * GET /api/fleet/drivers/:id/rest-state. Returns the rest gap
 * computed from the latest completed clock-out for the driver's
 * Employee, or `hasHistory:false` if the driver's never clocked in
 * via the platform. Threshold is 11h (EU 561 Art 8(2) standard rest).
 */
export async function fetchDriverRestState(
  driverId: string,
  signal?: AbortSignal,
): Promise<DriverRestStatePublic> {
  return apiFetch<DriverRestStatePublic>(
    `/api/fleet/drivers/${encodeURIComponent(driverId)}/rest-state`,
    { method: 'GET', signal },
  );
}

/**
 * 2026-06-03 Driver Tablet T3 phase 1 — POST a completed in-cab
 * session. Writes the drive / break / on-site / other-work breakdown
 * for the day; Phase D Driver Reported reads from it on the next
 * fetch. When finishChoice='home', the parent ClockIn is closed
 * server-side.
 */
export interface RecordDriverTabletSessionPayload {
  driverId: string;
  startedAt: string;
  endedAt: string;
  finishChoice: 'home' | 'office';
  driveSeconds: number;
  breakSeconds: number;
  onSiteSeconds: number;
  periodCount?: number;
  extensionUsed?: boolean;
  vehicleReg?: string | null;
}

export interface DriverTabletSessionPublic {
  id: string;
  driverId: string;
  employeeId: string;
  clockInId: string | null;
  sessionDate: string;
  startedAt: string;
  endedAt: string;
  finishChoice: 'home' | 'office';
  driveMinutes: number;
  breakMinutes: number;
  onSiteMinutes: number;
  otherWorkMinutes: number;
  periodCount: number;
  extensionUsed: boolean;
  vehicleReg: string | null;
}

export async function recordDriverTabletSession(
  payload: RecordDriverTabletSessionPayload,
): Promise<DriverTabletSessionPublic> {
  return apiFetch<DriverTabletSessionPublic>(
    `/api/fleet/driver-tablet/session`,
    { method: 'POST', body: payload },
  );
}

/**
 * 2026-06-04 Driver Tablet T3 phase 2 — immediate clock-in.
 *
 * Called the moment the driver passes the auth overlay on screen 0,
 * BEFORE the gate screen renders. Writes a ClockIn row to the
 * backend so HR / Fleet / Live Dashboard see the driver as clocked
 * in immediately. Idempotent — if there's already an open ClockIn
 * (kiosk this morning, or this tablet a minute ago that reloaded),
 * it returns the existing row instead of creating a duplicate.
 *
 * `clockedInAt` from the response should be used as the tablet's
 * own session start time — so a reload mid-shift picks up where the
 * driver was, not where the React state was wiped to.
 */
export interface DriverTabletClockInResult {
  clockInId: string;
  employeeId: string;
  clockedInAt: string;
  /** True when this call created the row; false when it returned an
   *  existing open ClockIn. */
  isNew: boolean;
}

// 2026-06-08 (TM rest-blocking directive) — payload carried on the 403
// DRIVER_RESTING error when a legally-resting driver is refused at clock-in
// (ApiError.details). The tablet + kiosk render the refused state from it.
export interface ClockInEligibilityPayload {
  requirement:
    | 'daily_regular'
    | 'daily_reduced'
    | 'weekly_regular'
    | 'weekly_reduced';
  applicableMinimumSeconds: number;
  restServedSeconds: number | null;
  remainingSeconds: number;
  earliestClockInIso: string | null;
  lastClockedOutAtIso: string | null;
}

export async function recordDriverTabletClockIn(
  driverId: string,
): Promise<DriverTabletClockInResult> {
  return apiFetch<DriverTabletClockInResult>(
    `/api/fleet/driver-tablet/clock-in`,
    { method: 'POST', body: { driverId } },
  );
}

/**
 * 2026-06-11 — Walkaround defect photo via the DEVICE-authenticated fleet
 * route (fixes "Your session has expired" mid-walkaround in the cab). The
 * general attachments uploader is user-login-only at both ends; this one
 * speaks both identities: user JWT when present (admin desktop preview),
 * device token otherwise — same fallback rule as apiFetch. Multipart, so
 * it builds FormData and fetches directly with the envelope unwrap.
 * The server derives the driver from the clock-in and files the same
 * walkaround_defect attachment + caption format the TM already reads.
 */
export interface WalkaroundPhotoUploadResult {
  attachment: { id: string };
}

export async function uploadDriverTabletWalkaroundPhoto(input: {
  file: File;
  clockInId: string;
  itemId: string;
  outcome: 'advisory' | 'fail';
  note?: string;
}): Promise<WalkaroundPhotoUploadResult> {
  const fd = new FormData();
  fd.append('file', input.file);
  fd.append('clockInId', input.clockInId);
  fd.append('itemId', input.itemId);
  fd.append('outcome', input.outcome);
  if (input.note) fd.append('note', input.note);

  const token = getAccessToken() ?? getDriverTabletToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(apiUrl('/api/fleet/driver-tablet/walkaround-photo'), {
    method: 'POST',
    headers,
    body: fd,
  });

  let envelope: unknown = null;
  try {
    envelope = await res.json();
  } catch {
    /* non-JSON body (proxy error page) — fall through to the throw below */
  }
  if (
    typeof envelope === 'object' &&
    envelope !== null &&
    'success' in envelope &&
    (envelope as { success: boolean }).success === true &&
    'data' in envelope
  ) {
    return (envelope as { data: WalkaroundPhotoUploadResult }).data;
  }
  const errBody =
    typeof envelope === 'object' && envelope !== null && 'error' in envelope
      ? (envelope as { error: { code: string; message: string } }).error
      : null;
  throw new ApiError(
    errBody?.code ?? 'UPLOAD_FAILED',
    errBody?.message ?? 'Photo upload failed',
    res.status,
  );
}

/**
 * 2026-06-04 Driver Tablet T3 phase 2 — Layer 2 activity event log.
 *
 * Each tablet screen transition POSTs an event. The latest event for
 * a clock-in is "what the driver is doing right now". Used by the
 * Live Drivers dashboard (Layer 3) AND by tablet reload to restore
 * the right screen.
 */
export type TabletActivity =
  | 'gate'
  | 'walkaround'
  | 'ready'
  | 'driving'
  | 'break'
  | 'on_site'
  | 'finished'
  | 'rest'
  // 2026-06-04 (FT-DTAB-CANCEL-BREAK) — accidental-break undo. Written
  // when the driver taps "Cancel — tapped by mistake" on the break
  // screen. Server allows it (driverTabletSessionService.ts +
  // routes/fleet/index.ts zod enum).
  | 'break_cancelled'
  // 2026-06-05 (FT-DTAB-A02) — Period of Availability. Spec activity
  // type, distinct from Driving / Other Work / Break / Rest. Does not
  // count as Working Time and does not advance the 4.5h drive clock.
  | 'poa'
  // 2026-06-05 (FT-DTAB-A10 MM phase 1) — Multi-manning role swap.
  // Audit-only marker emitted when driver/co-driver swap who's at
  // the wheel. Zero-duration event; the subsequent 'driving' or
  // 'poa' event carries duration as usual.
  | 'role_swap'
  // 2026-06-05 (FT-DTAB-#20) — Fuelling / services. Spec Other Work.
  // Counts as Working Time; does NOT advance the 4.5h drive clock.
  | 'fuelling'
  // 2026-06-05 (FT-DTAB-#27) — Walkaround critical defect audit
  // event. Zero-duration. Doesn't bucket. Written when a driver
  // flags a critical walkaround item as Fail so the TM can see it.
  | 'critical_defect'
  // 2026-06-07 (Part 4) — driver accepted a reduced (9–11h) daily rest at
  // clock-in. Audit-only; recorded via recordDriverTabletActivity.
  | 'reduced_rest_ack'
  // 2026-06-07 (audit gap #2) — Ferry/Train Derogation marker: the brief
  // vehicle movement on/off a ferry or train that interrupts a rest.
  | 'ferry_train';

export interface TabletActivityPublic {
  id: string;
  clockInId: string;
  driverId: string;
  employeeId: string;
  activity: string;
  vehicleReg: string | null;
  occurredAt: string;
}

export async function recordDriverTabletActivity(input: {
  clockInId: string;
  activity: TabletActivity;
  vehicleReg?: string | null;
}): Promise<TabletActivityPublic> {
  return apiFetch<TabletActivityPublic>(
    `/api/fleet/driver-tablet/activity`,
    { method: 'POST', body: input },
  );
}

export async function fetchDriverTabletCurrentActivity(
  clockInId: string,
  signal?: AbortSignal,
): Promise<TabletActivityPublic | null> {
  return apiFetch<TabletActivityPublic | null>(
    `/api/fleet/driver-tablet/current-activity?clockInId=${encodeURIComponent(clockInId)}`,
    { method: 'GET', signal },
  );
}

/**
 * 2026-06-04 — Per-ClockIn activity state. The tablet uses this on
 * mount to restore all the running timers from the backend rather
 * than starting them at zero each time the screen reloads. Returns
 * the same shape as one Live-Drivers row, scoped to one ClockIn.
 */
export interface DriverTabletActivityStatePublic {
  clockInId: string;
  clockedInAt: string;
  currentActivity: string | null;
  currentActivityAt: string | null;
  /** Seconds elapsed on the current activity (now - currentActivityAt). */
  currentActivitySeconds: number;
  currentVehicleReg: string | null;
  gateSeconds: number;
  walkaroundSeconds: number;
  readySeconds: number;
  driveSeconds: number;
  breakSeconds: number;
  onSiteSeconds: number;
  finishedSeconds: number;
  restSeconds: number;
  /** 2026-06-05 (FT-DTAB-A02) — Period of Availability total for this
   *  clock-in. Spec activity type; does not count as Working Time and
   *  does not advance the 4.5h drive clock. */
  poaSeconds: number;
  /** 2026-06-05 (FT-DTAB-#20) — Fuelling total. Spec Other Work.
   *  Counts as Working Time; does NOT advance the 4.5h drive clock. */
  fuellingSeconds: number;
  totalSeconds: number;
  /** EU 561 Art 7 — drive accumulated since the last FULL 45-min
   *  break (or 15+30 split). The 4.5h countdown on the driving
   *  screen uses THIS, not raw driveSeconds, so a 15-min break
   *  doesn't falsely reset the timer to zero. */
  continuousDriveSeconds: number;
  /** Break credit accrued toward the next 45-min reset. Lets the
   *  tablet show "Break credit: 15 min — need 30 more". */
  partialBreakSeconds: number;
  /** 2026-06-05 (FT-DTAB-A08) — WTR 6h Working Time counter.
   *  Working Time = Driving + Other Work; resets on any ≥15 min
   *  break or any Rest. POA does NOT count. Spec: 6h consecutive
   *  WT max without a Working Time Break (≥15 min). */
  continuousWorkingTimeSeconds: number;
  /** 2026-06-05 (FT-DTAB-A05) — Closed-session drive seconds for this
   *  driver in the current Fixed Week (Monday 00:00 → Sunday 24:00).
   *  Does NOT include the in-progress current clock-in. Tablet adds
   *  its live totalDriveSecs to display vs the 56h weekly cap. */
  weekDriveSecondsBefore: number;
  /** 2026-06-05 (FT-DTAB-A05) — Closed-session drive seconds for this
   *  driver in the rolling 14 days. Tablet adds live drive to display
   *  vs the 90h fortnightly cap. */
  fortnightDriveSecondsBefore: number;
  /** 2026-06-05 (FT-DTAB-A04) — Count of closed sessions in the
   *  current Fixed Week where the 10h drive extension was used.
   *  Tablet adds +1 for the current shift if extUsed, then gates
   *  the extension offer when total ≥ 2. */
  weekExtensionsBefore: number;
  /** 2026-06-05 (FT-DTAB-A11) — Working Time seconds for this driver
   *  in the current Fixed Week, from closed sessions only. Tablet
   *  adds live WT on top to compare against the WTR 60h cap. */
  weekWorkingTimeSecondsBefore: number;
  /** 2026-06-05 (FT-DTAB-A11) — Working Time seconds over the
   *  rolling 17 weeks (≈119 days). Tablet compares vs WTR 17-week
   *  reference period 816h cap (= 48h average × 17). */
  rolling17WeekWorkingTimeSeconds: number;
  /** Item 5 (2026-06-07) — the operator-elected WTR reference period the
   *  rolling Working Time above is measured over, plus its cap (816h for
   *  rolling-17, 1,248h for rolling-26). Defaults to rolling-17. */
  wtrReferencePeriod: 'rolling_17' | 'rolling_26';
  wtrReferenceCapSeconds: number;
  /** 2026-06-05 (FT-DTAB-A06) — Days worked since the most recent
   *  weekly rest (rest gap ≥45h), current in-progress shift = 1.
   *  Null = no weekly rest in the 21-day lookback. Tablet derives
   *  daysUntilRequired = max(0, 6 − daysWorked) per spec. */
  daysWorkedSinceLastWeeklyRest: number | null;
  /** 2026-06-05 (FT-DTAB-A03) — Count of reduced daily rests (9–11h
   *  gap) since the most recent weekly rest. Tablet warns at 3 per
   *  spec ("no more than 3 occasions between any 2 Weekly Rest
   *  Periods"). */
  reducedRestsSinceLastWeeklyRest: number;
  /** 2026-06-05 (FT-DTAB-A14) — Reduced WEEKLY rests (24–45h gaps)
   *  in the last 4 Fixed Weeks. Each carries hoursOwed (= 45 − gap)
   *  and the deadline (end of 3rd Fixed Week after) per spec. v1
   *  doesn't track "paid" — every reduction in the window is listed
   *  for visibility. */
  reducedWeeklyRests: Array<{
    reducedAtIso: string;
    hoursOwed: number;
    deadlineIso: string;
  }>;
}

export async function fetchDriverTabletActivityState(
  clockInId: string,
  signal?: AbortSignal,
): Promise<DriverTabletActivityStatePublic | null> {
  return apiFetch<DriverTabletActivityStatePublic | null>(
    `/api/fleet/driver-tablet/activity-state?clockInId=${encodeURIComponent(clockInId)}`,
    { method: 'GET', signal },
  );
}

// 2026-06-06 — Last clocked-out timestamp for an employee. Used by the
// driver-tablet rest screen to anchor the countdown to the actual
// server-side clock-out, not Date.now() at screen mount.
export interface LastClockedOutPublic {
  clockedOutAtIso: string;
  clockInId: string;
}

export async function fetchLastClockedOut(
  employeeId: string,
  signal?: AbortSignal,
): Promise<LastClockedOutPublic | null> {
  return apiFetch<LastClockedOutPublic | null>(
    `/api/fleet/driver-tablet/last-clocked-out?employeeId=${encodeURIComponent(employeeId)}`,
    { method: 'GET', signal },
  );
}

// 2026-06-05 (FT-DTAB-A14 phase 2) — Weekly Rest Compensation ledger.
export interface WeeklyRestCompensationPublic {
  id: string;
  driverId: string;
  employeeId: string;
  /** Display name from employees (first+last). null if missing. */
  driverName: string | null;
  reducedAtIso: string;
  restSeconds: number;
  hoursOwed: number;
  deadlineIso: string;
  paidAtIso: string | null;
  paidByClockInId: string | null;
  paidByUserId: string | null;
  paidNotes: string | null;
  overdue: boolean;
  // 2026-06-08 (A14) — TM acknowledgement of the overdue alert (distinct from
  // paid). Null until acknowledged.
  overdueAcknowledgedAt: string | null;
  overdueAcknowledgedBy: string | null;
  overdueAcknowledgedReason: string | null;
}

export async function fetchWeeklyRestCompensations(
  opts: { driverId?: string; unpaid?: boolean; overdue?: boolean } = {},
  signal?: AbortSignal,
): Promise<WeeklyRestCompensationPublic[]> {
  const params = new URLSearchParams();
  if (opts.driverId) params.set('driverId', opts.driverId);
  if (opts.unpaid) params.set('unpaid', 'true');
  if (opts.overdue) params.set('overdue', 'true');
  const qs = params.toString();
  return apiFetch<WeeklyRestCompensationPublic[]>(
    `/api/fleet/weekly-rest-compensation${qs ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
}

export async function markWeeklyRestCompensationPaid(input: {
  id: string;
  paidByClockInId: string;
  notes?: string | null;
}): Promise<WeeklyRestCompensationPublic> {
  return apiFetch<WeeklyRestCompensationPublic>(
    `/api/fleet/weekly-rest-compensation/${encodeURIComponent(input.id)}/mark-paid`,
    {
      method: 'POST',
      body: {
        paidByClockInId: input.paidByClockInId,
        notes: input.notes ?? null,
      },
    },
  );
}

export async function unmarkWeeklyRestCompensationPaid(
  id: string,
): Promise<WeeklyRestCompensationPublic> {
  return apiFetch<WeeklyRestCompensationPublic>(
    `/api/fleet/weekly-rest-compensation/${encodeURIComponent(id)}/unmark-paid`,
    { method: 'POST' },
  );
}
// 2026-06-08 (A14) — TM acknowledges an OVERDUE compensation alert (optional
// action note). Does NOT mark it paid — the owed rest stays tracked.
export async function acknowledgeOverdueCompensation(
  id: string,
  reason?: string,
): Promise<WeeklyRestCompensationPublic> {
  return apiFetch<WeeklyRestCompensationPublic>(
    `/api/fleet/weekly-rest-compensation/${encodeURIComponent(id)}/acknowledge-overdue`,
    { method: 'POST', body: reason ? { reason } : {} },
  );
}

// 2026-06-05 (FT-DTAB-A14 phase 3) — Eligible rests for the mark-paid
// picker. TM picks one of these instead of typing a ClockIn ID.
export interface EligibleRestPeriodPublic {
  clockInId: string;
  restStartIso: string;
  restEndIso: string;
  restSeconds: number;
  label: string;
}

export async function fetchEligibleRestPeriodsForDriver(
  driverId: string,
  signal?: AbortSignal,
): Promise<EligibleRestPeriodPublic[]> {
  return apiFetch<EligibleRestPeriodPublic[]>(
    `/api/fleet/weekly-rest-compensation/eligible-rests?driverId=${encodeURIComponent(driverId)}`,
    { method: 'GET', signal },
  );
}

// 2026-06-05 — Walkaround check records (DVSA evidence).
export interface WalkaroundCheckItemPublic {
  id: string;
  label: string;
  section: 'inside' | 'outside';
  critical: boolean;
  outcome: 'pass' | 'advisory' | 'fail';
  attachmentId?: string | null;
  caption?: string | null;
}
export interface WalkaroundCheckPublic {
  id: string;
  /** Null on manual desk entries (FLT-WA-RETIRE 2026-06-12). */
  clockInId: string | null;
  driverId: string;
  employeeId: string;
  driverName: string | null;
  /** 'tablet' = driver flow; 'manual' = logged at the desk. */
  source: 'tablet' | 'manual';
  vehicleReg: string;
  startedAtIso: string;
  completedAtIso: string;
  durationSeconds: number;
  totalItems: number;
  passCount: number;
  advisoryCount: number;
  failCount: number;
  criticalFailCount: number;
  items: WalkaroundCheckItemPublic[];
  // 2026-06-08 (#27 B) — TM acknowledgement of the critical-defect alert.
  // Null until acknowledged; only meaningful when criticalFailCount > 0.
  criticalAlertAcknowledgedAt: string | null;
  criticalAlertAcknowledgedBy: string | null;
  criticalAlertAcknowledgedReason: string | null;
}
export async function recordWalkaroundCheck(input: {
  clockInId: string;
  vehicleReg: string;
  startedAtIso: string;
  completedAtIso: string;
  items: WalkaroundCheckItemPublic[];
}): Promise<WalkaroundCheckPublic> {
  return apiFetch<WalkaroundCheckPublic>('/api/fleet/walkaround-checks', {
    method: 'POST',
    body: input,
  });
}
/** FLT-WA-RETIRE (2026-06-12) — manual desk entry. Same checklist item
 *  shape as the tablet; the row lands in walkaround_checks with
 *  source='manual' and no clock-in. */
export async function recordManualWalkaroundCheck(input: {
  vehicleId: string;
  driverId: string;
  checkDate: string;
  items: WalkaroundCheckItemPublic[];
}): Promise<WalkaroundCheckPublic> {
  return apiFetch<WalkaroundCheckPublic>('/api/fleet/walkaround-checks/manual', {
    method: 'POST',
    body: input,
  });
}
export async function listWalkaroundChecks(
  opts: { driverId?: string; vehicleReg?: string; from?: string; to?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<WalkaroundCheckPublic[]> {
  const params = new URLSearchParams();
  if (opts.driverId) params.set('driverId', opts.driverId);
  if (opts.vehicleReg) params.set('vehicleReg', opts.vehicleReg);
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return apiFetch<WalkaroundCheckPublic[]>(
    `/api/fleet/walkaround-checks${qs ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
}
// 2026-06-08 (#27 B) — TM acknowledges a critical-defect alert (optional
// action note). Returns the updated record.
export async function acknowledgeWalkaroundCritical(
  id: string,
  reason?: string,
): Promise<WalkaroundCheckPublic> {
  return apiFetch<WalkaroundCheckPublic>(
    `/api/fleet/walkaround-checks/${encodeURIComponent(id)}/acknowledge-critical`,
    { method: 'POST', body: reason ? { reason } : {} },
  );
}
export async function fetchWalkaroundCheck(
  id: string,
  signal?: AbortSignal,
): Promise<WalkaroundCheckPublic> {
  return apiFetch<WalkaroundCheckPublic>(
    `/api/fleet/walkaround-checks/${encodeURIComponent(id)}`,
    { method: 'GET', signal },
  );
}

// 2026-06-05 — TM Admin PIN gate.
export interface TmAdminPinStatus {
  pinSet: boolean;
  setAtIso: string | null;
}
export async function fetchTmAdminPinStatus(signal?: AbortSignal): Promise<TmAdminPinStatus> {
  return apiFetch<TmAdminPinStatus>('/api/fleet/tm-admin/pin-status', {
    method: 'GET',
    signal,
  });
}
export async function setTmAdminPin(pin: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/fleet/tm-admin/set-pin', {
    method: 'POST',
    body: { pin },
  });
}
export async function verifyTmAdminPin(pin: string): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/api/fleet/tm-admin/verify-pin', {
    method: 'POST',
    body: { pin },
  });
}

// 2026-06-06 — Transport Manager logins (outsourced role). A director /
// manager / admin provisions a Fleet-scoped login from the Admin hub.
export interface TmUserPublic {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}
export async function listTmUsers(signal?: AbortSignal): Promise<TmUserPublic[]> {
  return apiFetch<TmUserPublic[]>('/api/fleet/tm-users', {
    method: 'GET',
    signal,
  });
}
export async function createTmUser(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}): Promise<TmUserPublic> {
  return apiFetch<TmUserPublic>('/api/fleet/tm-users', {
    method: 'POST',
    body: input,
  });
}
export async function resetTmUserPassword(
  userId: string,
  password: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/fleet/tm-users/${encodeURIComponent(userId)}/reset-password`,
    { method: 'POST', body: { password } },
  );
}

// ─── Transport Manager credentials (2026-06-07) ──────────────────────────
// A TM's own certifications / qualifications + their certificate files.
export interface TmCredentialDocument {
  attachmentId: string;
  linkId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}
export interface TmCredentialPublic {
  id: string;
  userId: string;
  ownerName: string | null;
  credentialType: string;
  title: string;
  certificateNumber: string | null;
  issuingBody: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  notes: string | null;
  documents: TmCredentialDocument[];
  createdAt: string;
}
export interface TmCredentialInput {
  credentialType: string;
  title: string;
  certificateNumber?: string | null;
  issuingBody?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
  /** Director/manager recording ON BEHALF of a TM — the TM's userId.
   *  Ignored for a transport_manager (forced to self). Create only. */
  targetUserId?: string | null;
}
export async function listTmCredentials(
  signal?: AbortSignal,
): Promise<TmCredentialPublic[]> {
  return apiFetch<TmCredentialPublic[]>('/api/fleet/tm-credentials', {
    method: 'GET',
    signal,
  });
}
export async function createTmCredential(
  input: TmCredentialInput,
): Promise<TmCredentialPublic> {
  return apiFetch<TmCredentialPublic>('/api/fleet/tm-credentials', {
    method: 'POST',
    body: input,
  });
}
export async function updateTmCredential(
  id: string,
  input: TmCredentialInput,
): Promise<TmCredentialPublic> {
  return apiFetch<TmCredentialPublic>(
    `/api/fleet/tm-credentials/${encodeURIComponent(id)}`,
    { method: 'PUT', body: input },
  );
}
export async function deleteTmCredential(id: string): Promise<void> {
  await apiFetch<unknown>(
    `/api/fleet/tm-credentials/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}
export async function deleteTmCredentialDocument(linkId: string): Promise<void> {
  await apiFetch<unknown>(
    `/api/fleet/tm-credentials/documents/${encodeURIComponent(linkId)}`,
    { method: 'DELETE' },
  );
}
export interface ReminderRunResult {
  scanned: number;
  created: number;
  skipped: number;
}
/** Run the TM-credential expiry check now (this tenant) — the on-demand
 *  counterpart to the daily 07:00 worker cron. Director/manager/admin. */
export async function runTmCredentialReminders(): Promise<ReminderRunResult> {
  return apiFetch<ReminderRunResult>('/api/fleet/tm-credentials/run-reminders', {
    method: 'POST',
  });
}

// ─── AI assist #1 — infringement explainer (2026-06-07) ──────────────────
export interface InfringementExplanation {
  ruleTitle: string;
  ruleArticle: string;
  whatHappened: string;
  whyItBreaches: string;
  whatToDo: string;
}
/** Explain a detected infringement in plain English. Gated by the AI
 *  Assistance add-on — throws (code AI_ASSISTANCE_DISABLED) if it's off,
 *  or AI_NOT_CONFIGURED if no provider key is set for the tenant. */
export async function explainInfringement(input: {
  ruleCode: string;
  message: string;
  driverName?: string | null;
  severity?: string | null;
  occurredAt?: string | null;
}): Promise<InfringementExplanation> {
  return apiFetch<InfringementExplanation>('/api/fleet/infringements/explain', {
    method: 'POST',
    body: input,
  });
}

// ─── AI assist #7 — HGV planner sanity-check (2026-06-07) ─────────────────
export interface PlannerDayContext {
  day: string;
  start: string;
  driveHrs: number;
  siteHrs: number;
  restType: string;
}
export interface PlannerContext {
  driverName?: string | null;
  weekStart?: string | null;
  prevWeekHrs: number;
  extensionsUsed: number;
  reducedRestsUsed: number;
  totalDriveHrs: number;
  totalDutyHrs: number;
  fortnightDriveHrs: number;
  days: PlannerDayContext[];
}
export interface PlannerExplanation {
  summary: string;
  items: Array<{ issue: string; why: string; fix: string }>;
}
/** Explain the HGV planner's detected violations + suggest fixes for the
 *  site manager. Gated by the AI Assistance add-on. */
export async function explainPlannerViolations(input: {
  violations: string[];
  context: PlannerContext;
}): Promise<PlannerExplanation> {
  return apiFetch<PlannerExplanation>('/api/fleet/hgv-planner/explain', {
    method: 'POST',
    body: input,
  });
}

// ─── AI assist #6 — tacho per-driver week summary (2026-06-07) ───────────
export interface TachoWeekSummary {
  summary: string;
  patterns: string[];
  nearMisses: string[];
  utilisation: string;
}
/** Roll a driver's tacho week into a TM-facing summary (patterns,
 *  near-misses, utilisation). Gated by the AI Assistance add-on. */
export async function generateTachoWeekSummary(
  driverId: string,
  weekStart: string,
): Promise<TachoWeekSummary> {
  return apiFetch<TachoWeekSummary>('/api/fleet/tachograph/week-summary', {
    method: 'POST',
    body: { driverId, weekStart },
  });
}

// ─── AI assist — vehicle defect triage (2026-06-07) ──────────────────────
export interface DefectTriage {
  assessment: string;
  likelyCauses: string[];
  recommendedAction: string;
}
/** Triage a reported vehicle defect → seriousness read + likely causes +
 *  recommended action. Gated by the AI Assistance add-on. */
export async function triageVehicleDefect(input: {
  description: string;
  severity?: string | null;
  reportedDate?: string | null;
  vehicleReg?: string | null;
}): Promise<DefectTriage> {
  return apiFetch<DefectTriage>('/api/fleet/vehicle-defects/triage', {
    method: 'POST',
    body: input,
  });
}
/**
 * Upload a certificate file to a credential (multipart). apiFetch is
 * JSON-only, so build FormData + fetch directly — same pattern as
 * uploadTachoFile.
 */
export async function uploadTmCredentialDocument(
  credentialId: string,
  file: File,
  meta: {
    issuedDate?: string | null;
    expiryDate?: string | null;
    caption?: string | null;
  } = {},
): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  if (meta.issuedDate) fd.append('issuedDate', meta.issuedDate);
  if (meta.expiryDate) fd.append('expiryDate', meta.expiryDate);
  if (meta.caption) fd.append('caption', meta.caption);
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    apiUrl(`/api/fleet/tm-credentials/${encodeURIComponent(credentialId)}/documents`),
    { method: 'POST', headers, body: fd },
  );
  let envelope: unknown = null;
  try {
    envelope = await res.json();
  } catch {
    throw new Error('Upload failed');
  }
  if (
    typeof envelope === 'object' &&
    envelope !== null &&
    'success' in envelope &&
    (envelope as { success: boolean }).success === true
  ) {
    return;
  }
  if (typeof envelope === 'object' && envelope !== null && 'error' in envelope) {
    const err = (envelope as { error: { code: string; message: string } }).error;
    throw new Error(err.message || err.code || 'Upload failed');
  }
  throw new Error('Upload failed');
}

/** Tenant-wide live drivers feed — Layer 3 dashboard widget. */
export interface LiveDriverActivityPublic {
  clockInId: string;
  driverId: string | null;
  employeeId: string;
  employeeName: string | null;
  clockedInAt: string;
  clockInMethod: string;
  siteId: string | null;
  siteName: string | null;
  currentActivity: string | null;
  currentActivityAt: string | null;
  currentVehicleReg: string | null;
  /** Accumulated seconds per activity bucket since clock-in. The
   *  latest (still-in-progress) activity is counted up to "now". */
  gateSeconds: number;
  walkaroundSeconds: number;
  readySeconds: number;
  driveSeconds: number;
  breakSeconds: number;
  onSiteSeconds: number;
  finishedSeconds: number;
  restSeconds: number;
  /** 2026-06-05 (FT-DTAB-A02) — Period of Availability total for
   *  this clock-in. Spec activity type. Surfaced as its own column
   *  on Live Drivers so TM can see POA usage. */
  poaSeconds: number;
  /** 2026-06-05 (FT-DTAB-#20) — Fuelling total. Spec Other Work. */
  fuellingSeconds: number;
  totalSeconds: number;
  /** 2026-06-04 — Auto-detected compliance breaches for this
   *  clock-in. Map markers go red + pulse when there's any
   *  critical infringement; table shows "⛔ X" badge. */
  infringements: Array<{
    ruleCode: string;
    severity: 'critical' | 'warning';
    message: string;
    occurredAt: string;
  }>;
  /** 2026-06-04 FT-DTAB-PERIODS-01 — Period 1/2/3 split. Always
   *  three entries (index 1, 2, 3); UI only renders P3 row when it
   *  has any drive/break/on-site or a startedAt. */
  periods: Array<{
    index: 1 | 2 | 3;
    startedAt: string | null;
    endedAt: string | null;
    driveSeconds: number;
    breakSeconds: number;
    onSiteSeconds: number;
    resetKind: '45_single' | '15+30_split' | 'rest' | 'none_yet';
  }>;
}

export async function fetchLiveDriverActivity(
  signal?: AbortSignal,
): Promise<LiveDriverActivityPublic[]> {
  return apiFetch<LiveDriverActivityPublic[]>(
    `/api/fleet/driver-tablet/live`,
    { method: 'GET', signal },
  );
}

/**
 * 2026-06-04 — Tablet GPS location feed. The tablet POSTs every
 * ~30s while clocked in; the Live & GPS tab on Fleet reads
 * /live-locations to render markers + connect to the activity row
 * for the same driver.
 */
export async function recordDriverTabletLocation(input: {
  clockInId: string;
  lat: number;
  lng: number;
  accuracyM?: number | null;
  speedMps?: number | null;
  headingDeg?: number | null;
  reportedAt: string;
}): Promise<{ id: string; reportedAt: string }> {
  return apiFetch<{ id: string; reportedAt: string }>(
    `/api/fleet/driver-tablet/location`,
    { method: 'POST', body: input },
  );
}

export interface LiveTabletLocationPublic {
  clockInId: string;
  driverId: string | null;
  employeeId: string;
  employeeName: string | null;
  vehicleId: string | null;
  vehicleReg: string | null;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  reportedAt: string | null;
}

export async function fetchLiveTabletLocations(
  signal?: AbortSignal,
): Promise<LiveTabletLocationPublic[]> {
  return apiFetch<LiveTabletLocationPublic[]>(
    `/api/fleet/driver-tablet/live-locations`,
    { method: 'GET', signal },
  );
}

/** Trail (per-ClockIn fix history) for the day's path polyline. */
export interface TrailPointPublic {
  lat: number;
  lng: number;
  accuracyM: number | null;
  reportedAt: string;
}
export async function fetchDriverTabletTrail(
  clockInId: string,
  signal?: AbortSignal,
): Promise<TrailPointPublic[]> {
  return apiFetch<TrailPointPublic[]>(
    `/api/fleet/driver-tablet/location-trail?clockInId=${encodeURIComponent(clockInId)}`,
    { method: 'GET', signal },
  );
}

/** Site geofences for the live map circles. */
export interface SiteGeofencePublic {
  id: string;
  name: string;
  kind: string;
  lat: number;
  lng: number;
  radiusM: number;
  isPrimary: boolean;
}
export async function fetchSiteGeofences(
  signal?: AbortSignal,
): Promise<SiteGeofencePublic[]> {
  return apiFetch<SiteGeofencePublic[]>(`/api/fleet/site-geofences`, {
    method: 'GET',
    signal,
  });
}

/**
 * 2026-06-04 — Infringement workflow API. Dedicated Infringements
 * tab consumes fetchInfringementsForTenant; the acknowledge modal
 * called from there (and from the Live Drivers & GPS row badge)
 * posts to acknowledgeInfringement.
 */
export interface TenantInfringementPublic {
  id: string;
  clockInId: string;
  driverId: string;
  employeeId: string;
  driverName: string | null;
  vehicleReg: string | null;
  ruleCode: string;
  severity: 'critical' | 'warning';
  occurredAt: string;
  detectedAt: string;
  message: string;
  evidence: unknown;
  acknowledgedAt: string | null;
  acknowledgedReason: string | null;
  acknowledgedByName: string | null;
}

export async function fetchInfringementsForTenant(opts: {
  acknowledged?: boolean | undefined;
  driverId?: string | undefined;
  ruleCode?: string | undefined;
} = {}, signal?: AbortSignal): Promise<TenantInfringementPublic[]> {
  const params = new URLSearchParams();
  if (opts.acknowledged !== undefined) {
    params.set('acknowledged', String(opts.acknowledged));
  }
  if (opts.driverId) params.set('driverId', opts.driverId);
  if (opts.ruleCode) params.set('ruleCode', opts.ruleCode);
  const qs = params.toString();
  return apiFetch<TenantInfringementPublic[]>(
    `/api/fleet/infringements${qs ? `?${qs}` : ''}`,
    { method: 'GET', signal },
  );
}

export async function acknowledgeInfringement(
  id: string,
  reason: string,
): Promise<TenantInfringementPublic> {
  return apiFetch<TenantInfringementPublic>(
    `/api/fleet/infringements/${encodeURIComponent(id)}/acknowledge`,
    { method: 'POST', body: { reason } },
  );
}

/**
 * 2026-06-04 — Driver tablet next-gate notice. Returns the driver's
 * own unacknowledged infringements from the last 30 days. Used on
 * the gate screen to surface anything the TM hasn't signed off yet
 * so the driver is informed before starting their new shift.
 */
export interface DriverTabletMyInfringementPublic {
  id: string;
  clockInId: string;
  ruleCode: string;
  severity: 'critical' | 'warning';
  message: string;
  occurredAt: string;
}

export async function fetchMyUnackInfringements(
  driverId: string,
  signal?: AbortSignal,
): Promise<DriverTabletMyInfringementPublic[]> {
  return apiFetch<DriverTabletMyInfringementPublic[]>(
    `/api/fleet/driver-tablet/my-infringements?driverId=${encodeURIComponent(driverId)}`,
    { method: 'GET', signal },
  );
}

/**
 * TODAY'S ROSTERED SHIFT — TODO 097, 2026-09-07.
 *
 * Shane named two sources on 2026-09-05: *"The shift will come from the shift
 * pattern in site works or journey log in fleet"*. The tablet read one. An HGV
 * driver was on the planner; a van or car driver working to a site shift was on
 * no source the tablet could see, so it knew only that he had tapped the button.
 *
 * SITE WORKS COVERS BOTH van delivery and a site worker driving to a job —
 * Shane: *"Same thing they are using company vehicles whether they are
 * proceeding to. Site to work on site Or delivering as they are not working to
 * the HGV rules"*.
 *
 * NULL IS AN ORDINARY ANSWER. Plenty of days have nothing rostered, and the
 * screen says so rather than inventing a shift.
 *
 * NO CLOCK TIMES, and that is the table rather than an omission. The planner
 * holds a job, a site, an area and estimated travel/site HOURS. The HGV planner
 * does carry a start time — a real difference between the two schedules, not
 * something to paper over.
 */
export interface DriverScheduledShift {
  source: 'site_planner';
  date: string;
  jobLabel: string | null;
  siteName: string | null;
  description: string | null;
  area: string | null;
  /** Estimated hours as the planner holds them. 1.5 = 1h 30m. */
  travelHrs: number;
  siteHrs: number;
}

export async function fetchMyShift(
  driverId: string,
  signal?: AbortSignal,
): Promise<DriverScheduledShift | null> {
  return apiFetch<DriverScheduledShift | null>(
    `/api/fleet/driver-tablet/my-shift?driverId=${encodeURIComponent(driverId)}`,
    { method: 'GET', signal },
  );
}

/**
 * EVERYTHING THE DRIVER OWES, FOR THE TABLET — TODO 095 point 4, 2026-09-07.
 *
 * The sign-off writer went in earlier the same day and 095 was reported
 * complete. It was not: the tablet had no way to LIST or OPEN a talk, so the
 * driver was warned, blocked, and could do nothing — somebody had to sign it
 * off for him from a desk. Shane: *"This you reported as complete earlier"*.
 *
 * WIDENED MID-BUILD, on Shane's correction: *"It should read anything for a
 * driver on a driver tablet. If they have a policy , memo or toolbox talk or
 * fleet toolbox talk all should reach the driver as an employee ... which one
 * he uses first is what warns him or after the 28 days blocks him"*.
 *
 * So `outstanding` is the SAME list the kiosk advisory shows, and
 * `signableTalks` is the subset he can clear here and now. Two windows onto one
 * evaluator, which is why whichever surface he touches first is the one that
 * warns him.
 */
export interface DriverOutstandingItem {
  type: string;
  itemId: string;
  itemName: string;
  /** 4..1 = weeks left. 0 = blocking now. */
  level: number;
  detail: string;
}

export interface DriverSignableTalk {
  assignmentId: string;
  toolboxTalkId: string;
  title: string;
  content: string;
  keyMessage: string | null;
  revisionId: string | null;
  revisionNumber: number | null;
  deadline: string | null;
  overdue: boolean;
  hasQuestions: boolean;
}

export interface DriverOutstandingWork {
  outstanding: DriverOutstandingItem[];
  signableTalks: DriverSignableTalk[];
}

export async function fetchMyOutstanding(
  driverId: string,
  signal?: AbortSignal,
): Promise<DriverOutstandingWork> {
  return apiFetch<DriverOutstandingWork>(
    `/api/fleet/driver-tablet/my-outstanding?driverId=${encodeURIComponent(driverId)}`,
    { method: 'GET', signal },
  );
}

/**
 * Sign a toolbox talk off. The driver has read it on the tablet; this is the
 * writer that was already there and had no door to it.
 *
 * NOT the TM's acknowledgement, which clears a breach. This clears the
 * ASSIGNMENT, which is what unblocks him.
 */
export async function signOffToolboxTalk(
  assignmentId: string,
  body: { signatureData?: string | null; score?: number | null } = {},
): Promise<{ assignmentId: string; passed: boolean; alreadySigned: boolean }> {
  return apiFetch<{ assignmentId: string; passed: boolean; alreadySigned: boolean }>(
    `/api/fleet/toolbox/assignments/${encodeURIComponent(assignmentId)}/sign-off`,
    { method: 'POST', body },
  );
}

/* ===========================================================================
 * THE THREE ACCEPTANCES — TODO 098, 2026-09-07.
 *
 * Shane, 2026-09-06: *"All pop up in all areas be that platform , kiosk popup ,
 * tablet pop up have to be accepted"* and *"All have to be recorded"*.
 *
 * Each of these three used to be a `setState` and nothing else. The screens
 * were finished; the writers were missing. None of them refuses anything - the
 * gate they belong to has already fired by the time these are called.
 * ======================================================================== */

/**
 * The driver accepts his own infringement notice.
 *
 * NOT the TM's acknowledgement, which clears the breach and needs a reason.
 * This writes `driver_accepted_at` and clears nothing - so the notice keeps
 * reappearing at every clock-in until the TM signs it off, which is the same
 * rule the kiosk uses for outstanding compliance items.
 */
export async function acceptMyInfringements(
  driverId: string,
  infringementIds: string[],
): Promise<{ accepted: number }> {
  return apiFetch<{ accepted: number }>(
    '/api/fleet/driver-tablet/my-infringements/accept',
    { method: 'POST', body: { driverId, infringementIds } },
  );
}

/**
 * The driver acknowledges being refused clock-in for a legally required rest.
 *
 * The one refusal a manager PIN cannot override, and the only one that used to
 * leave no trace at all - the handler was `setRestBlock(null)`.
 */
export async function acceptRestBlock(input: {
  driverId: string;
  requirement: string;
  remainingSeconds: number;
  earliestClockInIso: string | null;
}): Promise<{ recorded: boolean }> {
  return apiFetch<{ recorded: boolean }>(
    '/api/fleet/driver-tablet/rest-block-acceptance',
    { method: 'POST', body: input },
  );
}

/**
 * The driver accepts a driving warning in the cab - licence, CPC, D4, tacho
 * card, DVLA check, overdue toolbox talk.
 *
 * He also accepts it at the kiosk clock-on, through the advisory path that was
 * already there. Both, on Shane's instruction, 2026-09-07: *"he may walk in to
 * the office and clock in with no driving that day a driving job comes up so he
 * needs to know"* - at clock-in nobody yet knows whether he will drive.
 */
export async function acceptDriverNotices(
  driverId: string,
  items: string[],
): Promise<{ recorded: boolean }> {
  return apiFetch<{ recorded: boolean }>(
    '/api/fleet/driver-tablet/driver-notice-acceptance',
    { method: 'POST', body: { driverId, items } },
  );
}

/**
 * 2026-06-03 Driver Tablet T1 — auth API. The dropdown picks the
 * driver; this set verifies identity via PIN or biometric (selective
 * per driver: biometric only when the tenant has it on AND the
 * driver's Employee has enrolled a credential).
 */
export interface DriverAuthOptionsPublic {
  driverId: string;
  driverName: string | null;
  biometricAvailable: boolean;
  pinAvailable: boolean;
}

export async function fetchDriverAuthOptions(
  driverId: string,
  signal?: AbortSignal,
): Promise<DriverAuthOptionsPublic> {
  return apiFetch<DriverAuthOptionsPublic>(
    `/api/fleet/driver-tablet/auth-options?driverId=${encodeURIComponent(driverId)}`,
    { method: 'GET', signal },
  );
}

/**
 * Driver Tablet T1.2 phase 4a (2026-06-03) — device identity + bound
 * vehicle for screen 0's "You are in vehicle AB12CDE" banner. The
 * backend returns `vehicle: null` when no vehicle is bound to this
 * tablet (legacy provisioning, kiosk-style tablets) OR when the call
 * comes in on a JWT (admin desktop preview). Either way the UI can
 * render the "not bound" banner.
 */
export interface DriverTabletDeviceInfoPublic {
  deviceName: string | null;
  /** The company's IANA time zone (TODO 135). The tablet holds a device token,
   *  not a company session, so this is how it learns which clock to show. */
  timezone: string;
  vehicle: {
    id: string;
    registration: string;
    make: string;
    model: string;
    /** none | chilled | frozen | dual, or NULL. Drives the refrigeration item
     *  on the walkaround — see walkaroundItemsFor. */
    temperatureControl: string | null;
  } | null;
}

/* ─────────────── Licence endorsements (2026-09-08) ─────────────── */

/**
 * One endorsement as the API returns it.
 *
 * `expiresOn` and `dateBasis` are COMPUTED server-side from the rules in
 * @jeanieiq/types, not stored. The client does not re-derive them: two copies
 * of a four-versus-eleven-year rule would eventually disagree, and the one on
 * screen would be the wrong one.
 */
export interface DriverEndorsementPublic {
  id: string;
  driverId: string;
  offenceCode: string;
  points: number;
  offenceDate: string;
  convictionDate: string | null;
  notes: string;
  /** When it comes off the licence, YYYY-MM-DD. */
  expiresOn: string;
  /**
   * Which date that expiry was computed from. 'offence-as-fallback' means the
   * rule wanted a conviction date, none was recorded, and the offence date was
   * used — which expires it EARLIER than it really does, so the screen says so.
   */
  dateBasis: 'offence' | 'conviction' | 'offence-as-fallback';
  createdAt: string;
}

export interface DriverPointsSummary {
  /** Points on endorsements still printed on the licence. */
  onLicence: number;
  /** Points inside the three-year totting window — the operational figure. */
  totting: number;
  liveCount: number;
  atTottingThreshold: boolean;
  /**
   * 'endorsements' — computed from the records, and accountable.
   * 'stored-total' — no records entered, so this is the legacy integer
   *   somebody typed. It has no dates behind it, so `totting` is reported
   *   equal to `onLicence`, which can only over-state.
   *
   * The screen MUST distinguish these. A typed-in number presented as a
   * computed one is a figure nobody can account for.
   */
  source: 'endorsements' | 'stored-total';
}

export async function fetchDriverEndorsements(
  driverId: string,
  signal?: AbortSignal,
): Promise<{ endorsements: DriverEndorsementPublic[]; points: DriverPointsSummary }> {
  return apiFetch<{ endorsements: DriverEndorsementPublic[]; points: DriverPointsSummary }>(
    `/api/fleet/drivers/${encodeURIComponent(driverId)}/endorsements`,
    { method: 'GET', signal },
  );
}

export interface AddDriverEndorsementPayload {
  offenceCode: string;
  points: number;
  offenceDate: string;
  convictionDate?: string | null;
  notes?: string;
}

export async function addDriverEndorsement(
  driverId: string,
  payload: AddDriverEndorsementPayload,
): Promise<DriverEndorsementPublic> {
  const data = await apiFetch<{ endorsement: DriverEndorsementPublic }>(
    `/api/fleet/drivers/${encodeURIComponent(driverId)}/endorsements`,
    /* The OBJECT, not a JSON string — apiFetch stringifies. tsc caught the
       string version, which would have posted a quoted blob. */
    { method: 'POST', body: payload },
  );
  return data.endorsement;
}

export async function removeDriverEndorsement(endorsementId: string): Promise<void> {
  await apiFetch<{ removed: boolean }>(
    `/api/fleet/endorsements/${encodeURIComponent(endorsementId)}`,
    { method: 'DELETE' },
  );
}

export async function fetchDriverTabletDeviceInfo(
  signal?: AbortSignal,
): Promise<DriverTabletDeviceInfoPublic> {
  return apiFetch<DriverTabletDeviceInfoPublic>(
    `/api/fleet/driver-tablet/device-info`,
    { method: 'GET', signal },
  );
}

/**
 * May this cab tablet be unpaired? — TODO 080a (2026-09-29).
 *
 * The tablet had no unpair control at all, so one paired to the wrong
 * vehicle could only be cleared by wiping browser storage by hand. It now
 * asks for the same Transport Manager PIN as the wall kiosk. Throws on
 * refusal; the caller shows the server's message, which names the next
 * action for each of TM_PIN_INVALID, TM_PIN_NOT_SET and
 * TM_PIN_RATE_LIMITED.
 */
export async function verifyDriverTabletUnpairPin(
  pin: string,
  signal?: AbortSignal,
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/fleet/driver-tablet/unpair/verify-pin`,
    { method: 'POST', body: { pin }, signal },
  );
}

export interface DriverBiometricChallengePublic {
  // Shape from @simplewebauthn/server's generateAuthenticationOptions.
  // We feed `options` straight to @simplewebauthn/browser's
  // startAuthentication(options) so the client doesn't need to know
  // its exact internals.
  options: unknown;
  employeeId: string;
}

export async function requestDriverBiometricChallenge(
  driverId: string,
): Promise<DriverBiometricChallengePublic> {
  return apiFetch<DriverBiometricChallengePublic>(
    `/api/fleet/driver-tablet/biometric-challenge`,
    { method: 'POST', body: { driverId } },
  );
}

export interface VerifyDriverIdentityPayload {
  driverId: string;
  method: 'pin' | 'biometric';
  pin?: string;
  biometricResponse?: unknown;
}

export interface VerifyDriverIdentityResult {
  ok: true;
  driverId: string;
  driverName: string | null;
  method: 'pin' | 'biometric';
}

export async function verifyDriverIdentity(
  payload: VerifyDriverIdentityPayload,
): Promise<VerifyDriverIdentityResult> {
  return apiFetch<VerifyDriverIdentityResult>(
    `/api/fleet/driver-tablet/verify`,
    { method: 'POST', body: payload },
  );
}

export async function fetchDrivers(signal?: AbortSignal): Promise<DriverPublic[]> {
  // API returns { drivers: DriverPublic[] } — unwrap to array so the page
  // component can iterate directly without a crash on .filter()/.map().
  const res = await apiFetch<{ drivers: DriverPublic[] }>('/api/fleet/drivers', {
    method: 'GET',
    signal,
  });
  return res.drivers ?? [];
}

/* ───────────────── Driver Hours Declaration (2026-09-02) ───────────────── */

/**
 * The Site Manager sheet a transport manager completes when tachograph
 * visibility is incomplete — an agency driver, or one arriving from another
 * employer. Signed on paper by both driver and TM.
 *
 * NOTE what is absent: there is no licence-number field. The form on screen
 * asks for one, but the full number lives only on the employee, encrypted, so
 * the server returns `licenceLast4` from the driver record and never accepts a
 * typed value. Do not add one here.
 */
export interface DeclaredWeekHours {
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
}

export interface DriverHoursDeclaration {
  id: string;
  driverId: string;
  driverName: string | null;
  licenceLast4: string | null;
  /** YYYY-MM-DD. */
  declarationDate: string;
  vehicleRegistration: string | null;
  hours: DeclaredWeekHours;
  /** Server-computed, so the list and the sheet cannot disagree. */
  totalHours: number;
  lastWeeklyRestAt: string | null;
  incidentsNote: string | null;
  receivedByName: string;
  /** PNG data URLs captured on screen. Null until signed. */
  driverSignature: string | null;
  driverSignedAt: string | null;
  tmSignature: string | null;
  tmSignedAt: string | null;
  /** Why the last amendment was made. Null when never amended. */
  lastEditReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveDriverHoursDeclarationInput {
  declarationDate: string;
  vehicleRegistration?: string | null;
  hours: DeclaredWeekHours;
  lastWeeklyRestAt?: string | null;
  incidentsNote?: string | null;
  receivedByName: string;
  driverSignature?: string | null;
  tmSignature?: string | null;
}

/**
 * An amendment. Only the fields present change; the driver is not among them —
 * a declaration belongs to whoever signed it.
 *
 * `editReason` is REQUIRED and at least 20 characters. An edit to a signed
 * statement that nobody has to justify is just an undocumented change, so the
 * server refuses one without a reason and the type refuses to compile one.
 */
export type UpdateDriverHoursDeclarationInput =
  Partial<SaveDriverHoursDeclarationInput> & { editReason: string };

export const EDIT_REASON_MIN_LENGTH = 20;

/** The declarations held against one driver, newest first. */
export async function fetchDriverHoursDeclarations(
  driverId: string,
  signal?: AbortSignal,
): Promise<DriverHoursDeclaration[]> {
  const res = await apiFetch<DriverHoursDeclaration[]>(
    `/api/fleet/drivers/${driverId}/hours-declarations`,
    { method: 'GET', signal },
  );
  return res ?? [];
}

export async function saveDriverHoursDeclaration(
  driverId: string,
  input: SaveDriverHoursDeclarationInput,
): Promise<DriverHoursDeclaration> {
  // apiFetch stringifies for you — pass the object. `body: JSON.stringify(x)`
  // type-checks against RequestInit and would send the server a JSON *string*,
  // which is the trap api.ts documents on the `body` option.
  return apiFetch<DriverHoursDeclaration>(
    `/api/fleet/drivers/${driverId}/hours-declarations`,
    { method: 'POST', body: input },
  );
}

/**
 * Amends a saved declaration. Addressed by its own id, not nested under the
 * driver — the driver cannot be changed by an edit.
 */
export async function updateDriverHoursDeclaration(
  declarationId: string,
  input: UpdateDriverHoursDeclarationInput,
): Promise<DriverHoursDeclaration> {
  return apiFetch<DriverHoursDeclaration>(
    `/api/fleet/hours-declarations/${declarationId}`,
    { method: 'PATCH', body: input },
  );
}

export async function fetchDriver(id: string, signal?: AbortSignal): Promise<{
  driver: DriverPublic;
  infringements: unknown[];
  documents: unknown[];
  toolboxAssignments: ToolboxTalkAssignmentPublic[];
}> {
  return apiFetch(`/api/fleet/drivers/${encodeURIComponent(id)}`, { method: 'GET', signal });
}

export async function updateDriver(
  id: string,
  payload: UpdateDriverPayload,
): Promise<DriverPublic> {
  return apiFetch<DriverPublic>(`/api/fleet/drivers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

/* ──────────────────── Incidents (Plus+ · FORS b3) ──────────────────── */

export interface FleetIncidentPublic {
  id: string;
  tenantId: string;
  incidentDate: string | null;
  incidentType: string;
  severity: string;
  driverId: string | null;
  driverName: string | null;
  vehicleId: string | null;
  vehicleReg: string | null;
  location: string;
  description: string;
  injuries: boolean;
  thirdPartyInvolved: boolean;
  thirdPartyDetails: string;
  rootCause: string;
  correctiveActions: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIncidentPayload {
  incidentDate: string;
  incidentType?: 'accident_injury' | 'accident_damage' | 'near_miss' | 'other';
  severity?: 'minor' | 'moderate' | 'serious';
  driverId?: string | null;
  vehicleId?: string | null;
  location?: string;
  description?: string;
  injuries?: boolean;
  thirdPartyInvolved?: boolean;
  thirdPartyDetails?: string;
  rootCause?: string;
  correctiveActions?: string;
  status?: 'reported' | 'investigating' | 'closed';
}

export async function fetchIncidents(
  signal?: AbortSignal,
): Promise<FleetIncidentPublic[]> {
  const res = await apiFetch<{ incidents: FleetIncidentPublic[] }>(
    '/api/fleet/incidents',
    { method: 'GET', signal },
  );
  return res.incidents ?? [];
}

export async function createIncident(
  payload: CreateIncidentPayload,
): Promise<FleetIncidentPublic> {
  return apiFetch<FleetIncidentPublic>('/api/fleet/incidents', {
    method: 'POST',
    body: payload,
  });
}

// ─── AI assist — incident investigation drafting (2026-06-07) ────────────
export interface IncidentDraft {
  rootCause: string;
  correctiveActions: string;
}
/** Draft a root cause + corrective actions from an incident account +
 *  context, for the manager to review and save. Gated by the AI
 *  Assistance add-on. */
export async function draftIncidentAnalysis(input: {
  description: string;
  incidentType?: string | null;
  severity?: string | null;
  location?: string | null;
  injuries?: boolean | null;
  thirdPartyInvolved?: boolean | null;
  thirdPartyDetails?: string | null;
}): Promise<IncidentDraft> {
  return apiFetch<IncidentDraft>('/api/fleet/incidents/draft', {
    method: 'POST',
    body: input,
  });
}

/* ──────────── FORS requirements dashboard live status (Plus+) ──────────── */

export interface ForsRequirementStatus {
  b3: 'open' | 'partial' | 'met';
  b4: 'open' | 'partial' | 'met';
}

export async function fetchForsRequirementStatus(
  signal?: AbortSignal,
): Promise<ForsRequirementStatus> {
  return apiFetch<ForsRequirementStatus>('/api/fleet/fors-status', {
    method: 'GET',
    signal,
  });
}

/* ────────────────── Fuel & emissions (Plus+ · FORS b4) ────────────────── */

export interface FleetFuelLogPublic {
  id: string;
  tenantId: string;
  vehicleId: string;
  vehicleReg: string | null;
  emissionsStandard: string | null;
  vehicleClass: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  litres: number;
  fuelCost: number | null;
  distanceMiles: number;
  mpg: number | null;
  notes: string;
  createdAt: string;
}

export interface CreateFuelLogPayload {
  vehicleId: string;
  periodStart: string;
  periodEnd: string;
  litres: number;
  fuelCost?: number | null;
  distanceMiles: number;
  notes?: string;
}

export async function fetchFuelLogs(
  signal?: AbortSignal,
): Promise<FleetFuelLogPublic[]> {
  const res = await apiFetch<{ fuelLogs: FleetFuelLogPublic[] }>(
    '/api/fleet/fuel',
    { method: 'GET', signal },
  );
  return res.fuelLogs ?? [];
}

export async function createFuelLog(
  payload: CreateFuelLogPayload,
): Promise<FleetFuelLogPublic> {
  return apiFetch<FleetFuelLogPublic>('/api/fleet/fuel', {
    method: 'POST',
    body: payload,
  });
}

/* ─────────────────────────── Journeys (Plus+) ─────────────────────────── */

export async function fetchJourneys(
  q: ListJourneysQuery = {},
  signal?: AbortSignal,
): Promise<JourneyPublic[]> {
  return apiFetch<JourneyPublic[]>('/api/fleet/journeys', {
    method: 'GET',
    query: {
      driverId: q.driverId ?? null,
      vehicleId: q.vehicleId ?? null,
      from: q.from ?? null,
      to: q.to ?? null,
    },
    signal,
  });
}

export async function createJourney(payload: CreateJourneyPayload): Promise<JourneyPublic> {
  return apiFetch<JourneyPublic>('/api/fleet/journeys', { method: 'POST', body: payload });
}

export async function updateJourney(
  id: string,
  payload: UpdateJourneyPayload,
): Promise<JourneyPublic> {
  return apiFetch<JourneyPublic>(`/api/fleet/journeys/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  });
}

/* ─────────────────────────── Tachograph (Plus+) ─────────────────────────── */

export async function fetchTachographUploads(
  signal?: AbortSignal,
): Promise<TachographUploadPublic[]> {
  return apiFetch<TachographUploadPublic[]>('/api/fleet/tachograph', { method: 'GET', signal });
}

export async function uploadTachograph(
  payload: UploadTachographPayload,
): Promise<TachographUploadPublic> {
  return apiFetch<TachographUploadPublic>('/api/fleet/tachograph', {
    method: 'POST',
    body: payload,
  });
}

export async function markAnalysed(
  id: string,
  payload: { infringementsFound: number },
): Promise<TachographUploadPublic> {
  return apiFetch<TachographUploadPublic>(
    `/api/fleet/tachograph/${encodeURIComponent(id)}/analysed`,
    { method: 'PATCH', body: payload },
  );
}

/* ─────────────────────────── Toolbox talks ─────────────────────────── */

export async function fetchTransportTalks(signal?: AbortSignal): Promise<ToolboxTalkPublic[]> {
  return apiFetch<ToolboxTalkPublic[]>('/api/fleet/toolbox', { method: 'GET', signal });
}

/**
 * Set the compliance-gate requirement on a Fleet toolbox talk - Stage F4.
 *
 * Shane, 2026-09-04: Fleet talks *"should gate the same as others as these are
 * key for drivers to be upto date"*.
 *
 * Every field optional, because the screen sends only what changed - flipping
 * the state must not silently reset a grace period the director set earlier.
 */
export async function setFleetTalkGateConfig(
  talkId: string,
  body: {
    gateIsActive?: boolean;
    gateIsRequired?: boolean;
    gracePeriodDays?: number;
    renewalMonths?: number | null;
  },
): Promise<{
  gateIsActive: boolean;
  gateIsRequired: boolean;
  gracePeriodDays: number;
  renewalMonths: number | null;
}> {
  // `body` is an object here, not a JSON string - apiFetch serialises it,
  // and the neighbouring mutations pass it the same way.
  return apiFetch(`/api/fleet/toolbox/${encodeURIComponent(talkId)}/gate-config`, {
    method: 'PUT',
    body,
  });
}

/** Give a published fleet talk to the drivers chosen, through read and sign; returns how many were asked. */
export async function assignTransportTalk(payload: AssignTransportTalkPayload): Promise<{ count: number }> {
  return apiFetch<{ count: number }>(
    `/api/fleet/toolbox/${encodeURIComponent(payload.toolboxTalkId)}/assign`,
    { method: 'POST', body: { employeeIds: payload.employeeIds } },
  );
}

/**
 * The country's one category list (Platform Admin -> Country Configuration ->
 * Categories, 2026-10-13), for the fleet talk builder's Category choice.
 */
export async function fetchFleetContentCategories(
  signal?: AbortSignal,
): Promise<Array<{ categoryKey: string; label: string }>> {
  const data = await apiFetch<{ categories: Array<{ categoryKey: string; label: string }> }>(
    '/api/fleet/content-categories',
    { method: 'GET', signal },
  );
  return data.categories;
}

export async function createToolboxTalk(input: ToolboxTalkInput): Promise<ToolboxTalkPublic> {
  return apiFetch<ToolboxTalkPublic>('/api/fleet/toolbox', { method: 'POST', body: input });
}

/** One of the company's own fleet talks, approved or waiting, for its editor. */
/** Publish a fleet talk from the Document Register: its revision in force to every driver, or those chosen. */
export function publishFleetTalk(
  talkId: string,
  audience?: PublishAudience,
): Promise<{ publishedRevision: number; asked: number }> {
  return apiFetch(`/api/fleet/toolbox/${encodeURIComponent(talkId)}/publish`, {
    method: 'POST', ...(audience ? { body: { audience } } : {}),
  });
}

export async function fetchToolboxTalk(talkId: string): Promise<ToolboxTalkPublic> {
  return apiFetch<ToolboxTalkPublic>(`/api/fleet/toolbox/${encodeURIComponent(talkId)}`);
}

export async function reviseToolboxTalk(
  talkId: string,
  input: ToolboxTalkInput & { changeSummary: string },
): Promise<ToolboxTalkPublic> {
  return apiFetch<ToolboxTalkPublic>(`/api/fleet/toolbox/${encodeURIComponent(talkId)}`, {
    method: 'PUT',
    body: input,
  });
}

export async function fetchToolboxTalkRevisions(
  talkId: string,
  signal?: AbortSignal,
): Promise<ToolboxTalkRevisionPublic[]> {
  return apiFetch<ToolboxTalkRevisionPublic[]>(
    `/api/fleet/toolbox/${encodeURIComponent(talkId)}/revisions`,
    { method: 'GET', signal },
  );
}

export async function fetchToolboxAssignments(
  q: { driverId?: string; toolboxTalkId?: string } = {},
  signal?: AbortSignal,
): Promise<ToolboxTalkAssignmentPublic[]> {
  return apiFetch<ToolboxTalkAssignmentPublic[]>('/api/fleet/toolbox/assignments', {
    method: 'GET',
    query: {
      driverId: q.driverId ?? null,
      toolboxTalkId: q.toolboxTalkId ?? null,
    },
    signal,
  });
}

/* ─────────────────────────── Site Works (Plus+) ─────────────────────────── */





/* ─────────────────────────── FORS (Plus+) ─────────────────────────── */

export async function fetchForsRecord(signal?: AbortSignal): Promise<FleetForsRecordPublic | null> {
  return apiFetch<FleetForsRecordPublic | null>('/api/fleet/fors', { method: 'GET', signal });
}

export async function upsertForsRecord(
  payload: UpsertForsPayload,
): Promise<FleetForsRecordPublic> {
  return apiFetch<FleetForsRecordPublic>('/api/fleet/fors', { method: 'PUT', body: payload });
}

/* ─────────────────────────── Import / Export ─────────────────────────── */

/**
 * Vehicles export endpoint returns text/csv. We do a raw fetch here so we
 * can read the response as text rather than the usual JSON envelope.
 */
export async function exportVehiclesCsv(): Promise<string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(apiUrl('/api/fleet/export/vehicles'), { method: 'GET', headers });
  if (!res.ok) {
    throw new Error('Export failed');
  }
  return res.text();
}

export async function previewImport(csv: string): Promise<VehicleImportPreviewResult> {
  return apiFetch<VehicleImportPreviewResult>('/api/fleet/import/vehicles', {
    method: 'POST',
    body: { csv },
  });
}

export async function confirmImport(
  rows: VehicleImportRow[],
): Promise<VehicleImportConfirmResult> {
  return apiFetch<VehicleImportConfirmResult>('/api/fleet/import/vehicles', {
    method: 'POST',
    body: { csv: 'placeholder', confirm: true, rows },
  });
}

/* ─────────────────────────── Sidebar badge ─────────────────────────── */

export async function fetchFleetExpiringCount(signal?: AbortSignal): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/fleet/expiring-count', {
    method: 'GET',
    signal,
  });
  return data.count;
}

/* ─────────────────── HGV Compliance Week Planner (2026-05-22) ────────── */

export type HgvPlannerRestType = 'normal' | 'reduced';
/** 2026-06-08 (A16/A17) — planned operation type. 'eu' (assimilated EU
 *  drivers' hours, the default) or 'gb_domestic' (GB Domestic Rules). The
 *  engine auto-switches to EU on any EU-regulated driving. */
export type HgvPlannerOperationType = 'eu' | 'gb_domestic';

/**
 * An ADDITIONAL delivery stop on a planner day (2026-09-02).
 *
 * Reagan: *"putting address in of where there driving to and then been able to
 * add additional drops to that day"*.
 *
 * The day's own fields still hold the drive to the FIRST stop, the time AT it,
 * and the drive home from the LAST one. A drop is a stop in between, so
 *
 *   day = [driveOutHrs → siteHrs] + [drop 1] … [drop n] + [driveBackHrs]
 *
 * and the day's total driving — which every HGV limit is measured against — is
 * driveOutHrs + every drop's driveHrs + driveBackHrs.
 */
export interface HgvPlannerDrop {
  /** 1-based. Assigned by the server from array order; not trusted on save. */
  sequence: number;
  /** Free text. Reagan: *"Just free text, typed each time"*. */
  address: string;
  /** What the driver is doing at this stop. Empty when unfilled. */
  purpose: string;
  /** Drive from the PREVIOUS address to this one. */
  driveHrs: number;
  /** Time spent at this drop. */
  siteHrs: number;
}

export interface HgvPlannerDay {
  /** 0 = Mon, 6 = Sun. */
  dayIndex: number;
  /** Free-text reg shown in the dropdown. */
  vehicleReg: string;
  /** Where the FIRST stop of the day is. Optional on older responses. */
  address?: string;
  /** What the driver is doing at the first stop. Optional on older responses. */
  purpose?: string;
  /** HH:MM (empty string = not yet set). */
  startTime: string;
  driveOutHrs: number;
  driveBackHrs: number;
  /** Multi-manning leg split: the co-driver's share of each leg. The
   *  self share stays in driveOutHrs / driveBackHrs; vehicle journey for
   *  a leg = self + co. 0 on solo days. */
  driveOutCoHrs: number;
  driveBackCoHrs: number;
  siteHrs: number;
  restType: HgvPlannerRestType;
  /** 2026-06-08 (A16/A17) — TM-planned operation type for the day. */
  operationType: HgvPlannerOperationType;
  /** Double-manned (two drivers crew the vehicle). Relaxes the daily
   *  rest to the EU 561 Art 8(5) 30h window = 21h dual driving window. */
  multiManned: boolean;
  /** The OTHER driver crewing the vehicle this day (null = solo). */
  coDriverId: string | null;
  /** 2026-06-05 (FT-DTAB-A10 MM phase 1) — Co-driver's employee name.
   *  Server enriches; null when solo or co-driver unresolved. */
  coDriverName: string | null;
  /** 2026-06-07 (Part 4/2) — read-only earliest legal start (HH:MM)
   *  computed server-side from the driver's actual last clock-out + the
   *  applicable rest. Present on reads; never sent on save. */
  earliestStart?: string | null;
  /**
   * Additional stops between the first one and the drive home, in order.
   *
   * Optional so a response predating drops still satisfies the type; absent
   * and empty both mean "no drops", which is what every day meant before
   * 2026-09-02.
   */
  drops?: HgvPlannerDrop[];
  /**
   * Who is riding in the cab without driving (2026-09-07).
   *
   * Reagan: *"sometimes a site guy would go with someone in the cab of the hgv
   * ... they dont need to be booked on the jounry calander as there not
   * driving"*.
   *
   * NOT a co-driver — `multiManned` / `coDriverId` mean a second qualified
   * driver and the planner reads them when working out the duty window.
   * Nothing on this list reaches that arithmetic. It exists so the SITE rota
   * knows the person is out.
   *
   * Optional so a response predating cab passengers still satisfies the type;
   * absent and empty both mean "nobody in the cab".
   */
  passengers?: HgvPlannerPassenger[];
}

/** Somebody in the cab who is not driving. See `HgvPlannerDay.passengers`. */
export interface HgvPlannerPassenger {
  employeeId: string;
  /** Resolved server-side from the employee row, so a client cannot assert a
   *  name for an id. Empty string on the way in; populated on the way out. */
  employeeName: string;
  /** Why they are along, as the planner typed it. May be empty. */
  note: string;
}

export interface HgvPlannerWeek {
  driverId: string;
  /** YYYY-MM-DD — always a Monday. */
  weekStartDate: string;
  prevWeekHrs: number;
  /** Always length 7, dayIndex 0..6 in order. */
  days: HgvPlannerDay[];
}

export interface SaveHgvPlannerWeekPayload {
  driverId: string;
  /** Server normalises any in-week date to its Monday before saving. */
  weekStartDate: string;
  prevWeekHrs?: number;
  days: HgvPlannerDay[];
}

/**
 * Load the planner for (driver, week). Returns a blank 7-day shape if
 * nothing's been saved for that pair yet — no 404 handling needed.
 */
export async function fetchHgvPlannerWeek(
  driverId: string,
  weekStartDate: string,
  signal?: AbortSignal,
): Promise<HgvPlannerWeek> {
  const data = await apiFetch<{ week: HgvPlannerWeek }>(
    `/api/fleet/hgv-planner?driverId=${encodeURIComponent(driverId)}&weekStart=${encodeURIComponent(weekStartDate)}`,
    { method: 'GET', signal },
  );
  return data.week;
}

/**
 * Replace the planner state for (driver, week). Upserts the week +
 * replaces all 7 days in a single server-side transaction.
 */
export async function saveHgvPlannerWeek(
  payload: SaveHgvPlannerWeekPayload,
): Promise<HgvPlannerWeek> {
  const data = await apiFetch<{ week: HgvPlannerWeek }>('/api/fleet/hgv-planner', {
    method: 'PUT',
    body: payload,
  });
  return data.week;
}

/** A week as returned by the monthly-range endpoint. Same shape as
 *  HgvPlannerWeek plus driverName for the calendar chip label. */
export interface HgvPlannerWeekWithDriver extends HgvPlannerWeek {
  driverName: string;
  /** When the week was last saved, ISO. */
  updatedAt: string;
  /**
   * Who last saved it. Null means we do not KNOW who — a week saved before
   * 2026-09-02, a save with no user on the request, or a saver whose account
   * has since been deleted. It never means nobody saved it.
   */
  lastSavedByName: string | null;
}

/**
 * Load every saved week within a date window across ALL drivers in
 * the current tenant. Used by the Fleet monthly calendar to render
 * per-driver coverage chips on each day.
 */
export async function fetchHgvPlannerRange(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<HgvPlannerWeekWithDriver[]> {
  const data = await apiFetch<{ weeks: HgvPlannerWeekWithDriver[] }>(
    `/api/fleet/hgv-planner/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { method: 'GET', signal },
  );
  return data.weeks;
}
/**
 * Every saved planner week, most recent first — the Planned weeks list.
 *
 * Not the range read with a wide window: this asks "what has been planned at
 * all", and a window wide enough to answer that has edges that silently hide
 * weeks beyond them.
 */
/**
 * Ask for the journeys of one planner week to be generated.
 *
 * The transport-manager trigger. Generation also runs automatically when a
 * driver finishes on the tablet; this is for a week whose days completed
 * before that existed, and for seeing the log fill without waiting on tablet
 * traffic. Idempotent — asking twice updates rather than duplicates.
 */
export async function generateJourneysFromPlan(input: {
  driverId: string;
  weekStart: string;
}): Promise<Array<{ journeyDate: string; legCount: number; unplanned: boolean; created: boolean }>> {
  const data = await apiFetch<{
    journeys: Array<{ journeyDate: string; legCount: number; unplanned: boolean; created: boolean }>;
  }>('/api/fleet/journeys/generate', { method: 'POST', body: input });
  return data.journeys;
}
export async function fetchSavedHgvPlannerWeeks(
  signal?: AbortSignal,
): Promise<HgvPlannerWeekWithDriver[]> {
  const data = await apiFetch<{ weeks: HgvPlannerWeekWithDriver[] }>(
    '/api/fleet/hgv-planner/saved',
    { method: 'GET', signal },
  );
  return data.weeks;
}


/* ─────────────────── Making someone a driver (HR › Licences) ──────────
 * hasDriver: they are a driver. isFleetDriver: a regulated fleet driver (EU/HGV
 * drivers' hours apply); false, one who drives for work. Setting either makes
 * them a driver and brings their HR licence and HGV details through. */

export interface DriverFleetStatus {
  isFleetDriver: boolean;
  hasDriver: boolean;
}

export async function getDriverFleetStatus(
  employeeId: string,
  signal?: AbortSignal,
): Promise<DriverFleetStatus> {
  return apiFetch<DriverFleetStatus>(
    `/api/fleet/employees/${employeeId}/fleet-driver-status`,
    { method: 'GET', signal },
  );
}

export async function setDriverFleetStatus(
  employeeId: string,
  isFleetDriver: boolean,
): Promise<DriverFleetStatus> {
  return apiFetch<DriverFleetStatus>(
    `/api/fleet/employees/${employeeId}/fleet-driver-status`,
    { method: 'PUT', body: { isFleetDriver } },
  );
}

export interface VehicleFleetStatus {
  isFleetVehicle: boolean;
}

export async function getVehicleFleetStatus(
  vehicleId: string,
  signal?: AbortSignal,
): Promise<VehicleFleetStatus> {
  return apiFetch<VehicleFleetStatus>(
    `/api/fleet/vehicles/${vehicleId}/fleet-status`,
    { method: 'GET', signal },
  );
}

export async function setVehicleFleetStatus(
  vehicleId: string,
  isFleetVehicle: boolean,
): Promise<VehicleFleetStatus> {
  return apiFetch<VehicleFleetStatus>(
    `/api/fleet/vehicles/${vehicleId}/fleet-status`,
    { method: 'PUT', body: { isFleetVehicle } },
  );
}

/* ─────────────────── Site Works Week Planner (2026-05-22) ────────────
 * Sister to HGV Planner. Same week+day shape, simpler payload — no
 * cumulative-hours rules to enforce so no prevWeekHrs / restType. */

/**
 * One additional STOP on a day entry (2026-09-07).
 *
 * Reagan: *"you might want to book another one in from this place to anyther
 * place kinda like the jounry log one where youat a stop"*.
 *
 * The day row is the FIRST location of a job; these are the places the engineer
 * moves on to. `travelHrs` is ONE LEG — the drive from the previous location to
 * this stop — unlike the day's own figure, which the travel-zone convention
 * treats as a round trip.
 */
export interface SitePlannerStop {
  /** 1-based position in the chain. The server re-numbers from the order it
   *  receives, so a client need not keep this tidy mid-edit. */
  sequence: number;
  siteName: string;
  /** Survey, install, repair, commissioning, other. Free text; the UI offers
   *  the list rather than the type constraining it. */
  purpose: string;
  /** Carried so the travel-zone auto-fill works on a stop as it does on a day. */
  area: string;
  /** The leg from the previous location to here. */
  travelHrs: number;
  siteHrs: number;
}

export interface SitePlannerDay {
  /** 0 = Mon, 6 = Sun. */
  dayIndex: number;
  /**
   * Which job this is within that day — 0 for the first, 1 for a second
   * (2026-09-06). `days` therefore holds AT LEAST seven entries, not exactly
   * seven: each dayIndex appears at least once, plus one more per extra job.
   *
   * Group by `dayIndex`; never treat the array position as the day.
   */
  entryIndex: number;
  /** Free-text Job label (replaced the old /jobs picker 2026-05-22). */
  jobLabel: string;
  /** Free-text site / job title. */
  siteName: string;
  /** What the engineer is doing on site. */
  description: string;
  /** Area of work (e.g. "London"). */
  area: string;
  /** Estimated travel hours (decimal, 1.5 = 1h 30m). Round trip by the
   *  travel-zone convention; a stop's own figure is a single leg. */
  travelHrs: number;
  /** Estimated on-site duration hours (decimal). */
  siteHrs: number;
  /**
   * Where this job goes on to after its first location, in chain order.
   *
   * Empty for most days. Nested inside the day rather than a flat list on the
   * week because the server re-numbers `entryIndex` from the order it receives
   * — a flat list keyed on this client's numbering would re-point at the wrong
   * job the moment somebody deleted the first of three jobs before saving.
   */
  stops: SitePlannerStop[];
}

export interface SitePlannerWeek {
  employeeId: string;
  /** YYYY-MM-DD — always a Monday. */
  weekStartDate: string;
  /** Always length 7, dayIndex 0..6 in order. */
  days: SitePlannerDay[];
}

export interface SaveSitePlannerWeekPayload {
  employeeId: string;
  /** Server normalises any in-week date to its Monday before saving. */
  weekStartDate: string;
  days: SitePlannerDay[];
  /**
   * Save over somebody's approved leave, on a second answer (2026-09-09).
   *
   * The first save is refused with `ENGINEER_ON_LEAVE` and the days named in
   * `error.details.unavailable`; the planner asks; a second save carries this.
   * Reagan's rule was that booking over leave must not happen QUIETLY, not that
   * it must be impossible — so there is a way through and it is never the
   * default.
   */
  ignoreLeave?: boolean;
}

/**
 * Load the planner for (employee, week). Returns a blank 7-day shape
 * if nothing's been saved for that pair yet — no 404 handling needed.
 */
/**
 * Guarantee every day carries a stops array (2026-09-07).
 *
 * The type says `stops: SitePlannerStop[]` and the server always sends one, so
 * this looks redundant — it is not. During a rolling deploy the web bundle can
 * be newer than the API, and a day arriving without the field turns
 * `d.stops.map(...)` into a TypeError that takes the whole planner down to a
 * white screen. A missing chain is not worth an unusable page.
 *
 * Done HERE, at the one boundary the data crosses, rather than with a `?? []`
 * at each of the dozen places that read it — those drift, and a reader cannot
 * tell which ones are load-bearing.
 */
function withStops<T extends { days: SitePlannerDay[] }>(week: T): T {
  return {
    ...week,
    days: (week.days ?? []).map((d) => ({ ...d, stops: d.stops ?? [] })),
  };
}
export async function fetchSitePlannerWeek(
  employeeId: string,
  weekStartDate: string,
  signal?: AbortSignal,
): Promise<SitePlannerWeek> {
  const data = await apiFetch<{ week: SitePlannerWeek }>(
    `/api/fleet/site-planner?employeeId=${encodeURIComponent(employeeId)}&weekStart=${encodeURIComponent(weekStartDate)}`,
    { method: 'GET', signal },
  );
  return withStops(data.week);
}

/**
 * Replace the planner state for (employee, week). Upserts the week +
 * replaces all 7 days in a single server-side transaction.
 */
export async function saveSitePlannerWeek(
  payload: SaveSitePlannerWeekPayload,
): Promise<SitePlannerWeek> {
  const data = await apiFetch<{ week: SitePlannerWeek }>('/api/fleet/site-planner', {
    method: 'PUT',
    body: payload,
  });
  return data.week;
}

/* ─── Assign one job to several engineers (2026-09-06) ─────────────── */

export interface AssignSiteWorkPayload {
  employeeIds: string[];
  /** Local YYYY-MM-DD. */
  dates: string[];
  jobLabel: string;
  siteName: string;
  description: string;
  area: string;
  travelHrs: number;
  siteHrs: number;
  /**
   * What to do when a day already holds different work:
   *   'report'    (default) write nothing, return the clashes
   *   'replace'   this job becomes the day's only work
   *   'alongside' add it as a SECOND job on that day
   *
   * 'alongside' only works from 2026-09-06 — before that a day was one row.
   */
  onClash?: 'report' | 'replace' | 'alongside';
  /** Book people onto days they are on approved leave anyway. Default false. */
  ignoreLeave?: boolean;
}

/* ─── Planned weeks — every saved Site Works week (2026-09-06) ─────── */

export interface SitePlannerSavedWeek extends SitePlannerWeekWithEngineer {
  /** Distinct DATES carrying work — a day with two jobs counts once. */
  daysPlanned: number;
  siteHrs: number;
  travelHrs: number;
  updatedAt: string;
}

/**
 * Every saved week, most recent first, across all engineers.
 *
 * The Journey Log equivalent, which Reagan asked for there: *"see the weeks
 * that are planned rather than changing in on the week by week."* Weeks with no
 * work on them are omitted server-side — picking an engineer in the planner
 * creates the week row before anything is typed.
 */
export async function fetchSavedSiteWeeks(
  limit?: number,
  signal?: AbortSignal,
): Promise<SitePlannerSavedWeek[]> {
  const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  const data = await apiFetch<{ weeks: SitePlannerSavedWeek[] }>(
    `/api/fleet/site-planner/saved${qs}`,
    { method: 'GET', signal },
  );
  return data.weeks;
}

/* ─── Who is away, for either planner (2026-09-06) ─────────────────── */

export interface PlannerLeaveEntry {
  employeeId: string;
  /** Null when the employee is not on the driver register. */
  driverId: string | null;
  name: string;
  /** Inclusive, local YYYY-MM-DD. */
  startDate: string;
  endDate: string;
  reason: string;
}

/**
 * Approved leave overlapping a window, for the HGV planner and the Site Works
 * screens both. Rows carry employeeId AND driverId so each planner reads the id
 * it holds — one definition of "away" rather than two that drift.
 */
export async function fetchPlannerLeave(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<PlannerLeaveEntry[]> {
  const data = await apiFetch<{ leave: PlannerLeaveEntry[] }>(
    `/api/fleet/planner-leave?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { method: 'GET', signal },
  );
  return data.leave;
}

/**
 * Who is out in an HGV cab in a window — the SITE rota's overlay (2026-09-07).
 *
 * Reagan: *"when it adds passenger on the journey log we need to make sure it
 * shows up on the site works week for that employee. so they know when booking
 * site work that employee is not available that day"*.
 *
 * The fact is entered once, on the journey, and the site calendar, overview and
 * week planner all read it — so nobody keys the same day twice and the two
 * rotas cannot drift apart.
 *
 * Sister to `fetchPlannerLeave`: to a site planner, a person in a cab and a
 * person on holiday are the same kind of fact. The one difference is that each
 * row here is a single DATE rather than a span.
 */
export interface CabPassengerEntry {
  employeeId: string;
  name: string;
  /** Local YYYY-MM-DD. One row per day, not a range. */
  date: string;
  /** The driver they are out with. Empty when unresolvable. */
  driverName: string;
  /** The vehicle, when the day has one. */
  vehicleReg: string;
  /** Why they are along, as the planner typed it. May be empty. */
  note: string;
}

/**
 * Find every day carrying a job number, across ALL TIME (2026-09-07).
 *
 * Reagan: *"on site works overview can we have a serach box where you can type
 * the job number in to search if you have booked that job it."*
 *
 * NO DATE RANGE, and that is the point. The question is "have we booked this
 * job", which does not belong to a period — a search over the rows already on
 * screen would answer "no" for a job booked in another month, which is the same
 * class of wrong answer as a failed read rendering as "nothing planned". Reagan
 * chose this shape when asked: *"Every booking, any date"*.
 */
export interface SiteJobMatch {
  /** Local YYYY-MM-DD. */
  date: string;
  employeeId: string;
  engineerName: string;
  /** The week to open, so a match is one click from the planner. */
  weekStartDate: string;
  jobLabel: string;
  siteName: string;
  description: string;
  area: string;
  siteHrs: number;
  travelHrs: number;
  /** How many further places that job runs to on that day. */
  stopCount: number;
}

export interface SiteJobSearchResult {
  /** Echoed back, so a slow response cannot be read against a newer query. */
  query: string;
  matches: SiteJobMatch[];
  /** The 200 cap trimmed the list — say so rather than imply the job is booked
   *  exactly 200 times. */
  truncated: boolean;
}

export async function searchSiteWorkByJob(
  q: string,
  signal?: AbortSignal,
): Promise<SiteJobSearchResult> {
  return apiFetch<SiteJobSearchResult>(
    `/api/fleet/site-planner/job-search?q=${encodeURIComponent(q)}`,
    { method: 'GET', signal },
  );
}

/**
 * Who to call when a driver is grounded (2026-09-07).
 *
 * Reagan: *"want the numbers tm and director ones ... on the site amanager you
 * can put these numebrs in on your side. then these will eventually feed to the
 * tablet that the driver will see as there the ones that need the numbers."*
 *
 * The Site Manager escalation list had four primary buttons and no handler on
 * any of them, because nothing in the platform held a phone number for a
 * transport manager or a director.
 */
/* Four rungs. Mirrors ESCALATION_SLOTS in escalationContactService and the
   CHECK narrowed by 20260908155458 — a fifth value here with no editor in
   EscalationContactsPanel is SM-06, a number that saves and disappears. */
export type EscalationSlot =
  | 'transport_manager'
  | 'director'
  | 'out_of_hours'
  | 'workshop';

export interface EscalationContact {
  slot: EscalationSlot;
  name: string;
  phone: string;
  notes: string;
  /** Null when nobody has ever saved this rung. */
  updatedAt: string | null;
}

/** Every rung, blanks included — an unfilled slot must not look like a failed
 *  read, so the server returns all five and the screen shows which are empty. */
export async function fetchEscalationContacts(
  signal?: AbortSignal,
): Promise<EscalationContact[]> {
  const data = await apiFetch<{ contacts: EscalationContact[] }>(
    '/api/fleet/escalation-contacts',
    { method: 'GET', signal },
  );
  return data.contacts;
}

/** Save one rung. An EMPTY phone is valid and clears the number — refusing it
 *  would leave no way to remove one that has changed hands. */
export async function saveEscalationContact(input: {
  slot: EscalationSlot;
  name?: string;
  phone?: string;
  notes?: string;
}): Promise<EscalationContact> {
  const data = await apiFetch<{ contact: EscalationContact }>(
    '/api/fleet/escalation-contacts',
    { method: 'PUT', body: input },
  );
  return data.contact;
}

export async function fetchCabPassengers(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<CabPassengerEntry[]> {
  const data = await apiFetch<{ passengers: CabPassengerEntry[] }>(
    `/api/fleet/cab-passengers?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { method: 'GET', signal },
  );
  return data.passengers;
}

export interface AssignSiteWorkUnavailable {
  employeeId: string;
  engineerName: string;
  date: string;
  /** "Holiday", "Sick", "TOIL" — the platform's own leave vocabulary. */
  reason: string;
}

/**
 * Somebody out in an HGV cab on a day being booked (2026-09-07).
 *
 * Reported beside the clashes rather than refused outright: a cab day is closer
 * to BUSY than to ABSENT, and the commonest reason to be in a cab is going to
 * the very job being planned — so refusing would refuse the ordinary case.
 *
 * Its own array and not part of `clashes`, because the two answers to a clash —
 * add alongside, or replace — both act on site planner days. A cab day lives in
 * the HGV planner and cannot be replaced from here.
 */
export interface AssignSiteWorkInCab {
  employeeId: string;
  engineerName: string;
  date: string;
  /** Who they are out with. Empty when unresolvable. */
  driverName: string;
  /** The lorry, when the day has one. */
  vehicleReg: string;
}

export interface AssignSiteWorkClash {
  employeeId: string;
  engineerName: string;
  date: string;
  existing: string;
}

/**
 * One job, several engineers, one or more days — in a single call.
 *
 * Deliberately NOT a loop over `saveSitePlannerWeek` in the browser: that
 * endpoint REPLACES all seven day rows of a week, so a loop would have to read
 * each week, patch a day and write the whole week back, silently overwriting
 * anything another planner saved in between. This touches only the named days.
 *
 * `written` counts engineer-day rows. `clashes` non-empty with `written: 0`
 * means nothing was saved — show the list and offer to overwrite.
 */
export async function assignSiteWork(
  payload: AssignSiteWorkPayload,
): Promise<{
  written: number;
  clashes: AssignSiteWorkClash[];
  unavailable: AssignSiteWorkUnavailable[];
  inCab: AssignSiteWorkInCab[];
}> {
  return apiFetch<{
    written: number;
    clashes: AssignSiteWorkClash[];
    unavailable: AssignSiteWorkUnavailable[];
    inCab: AssignSiteWorkInCab[];
  }>(
    '/api/fleet/site-planner/assign',
    { method: 'POST', body: payload },
  );
}

/** A week as returned by the monthly-range endpoint. Same shape as
 *  SitePlannerWeek plus engineerName for the calendar chip label. */
export interface SitePlannerWeekWithEngineer extends SitePlannerWeek {
  engineerName: string;
}

/**
 * Load every saved week within a date window across ALL engineers in
 * the current tenant. Used by slice C's monthly calendar.
 */
export async function fetchSitePlannerRange(
  from: string,
  to: string,
  signal?: AbortSignal,
): Promise<SitePlannerWeekWithEngineer[]> {
  const data = await apiFetch<{ weeks: SitePlannerWeekWithEngineer[] }>(
    `/api/fleet/site-planner/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    { method: 'GET', signal },
  );
  return data.weeks.map(withStops);
}

/* ─── Site Works Planner — slice D (planned vs actual) ─────────────── */

export interface SitePlannerDayActual {
  dayIndex: number; // 0=Mon..6=Sun
  /** Hours from closed clock-in sessions plus any live open session
   *  accrued to now. Trivial closed sessions ≤2 min are excluded. */
  actualHrs: number;
  /** True when at least one clock-in on this date is still open. */
  hasOpenSession: boolean;
  /** True when the engineer clocked in at all on this date. False
   *  = "No clock-in" line on the day card. */
  hasAnyClockIn: boolean;
  /** 2026-06-11 (Shane) — tablet day-log breakdown (van runs + cab
   *  tablets alike): driving → travel, on_site → site work. Hours. */
  tabletDrivingHrs: number;
  tabletSiteHrs: number;
  tabletFuellingHrs: number;
  tabletBreakHrs: number;
  hasTabletLog: boolean;
}

export interface SitePlannerWeekActuals {
  employeeId: string;
  weekStartDate: string; // YYYY-MM-DD (the Monday)
  days: SitePlannerDayActual[]; // length 7, day_index 0..6 in order
}

/**
 * Load actual clock-in/out totals per day for an engineer + week.
 * Sourced from the ClockIn (kiosk) table. Used by the Site Works
 * Planner's planned-vs-actual variance line on each day card.
 */
export async function fetchSitePlannerActuals(
  employeeId: string,
  weekStartDate: string,
  signal?: AbortSignal,
): Promise<SitePlannerWeekActuals> {
  const data = await apiFetch<{ actuals: SitePlannerWeekActuals }>(
    `/api/fleet/site-planner/actuals?employeeId=${encodeURIComponent(employeeId)}&weekStart=${encodeURIComponent(weekStartDate)}`,
    { method: 'GET', signal },
  );
  return data.actuals;
}

/* ─── Site Works Planner — slice E (Travel Zones lookup) ───────────── */

export interface SitePlannerTravelZone {
  id: string;
  name: string;
  defaultTravelHrs: number;
  notes: string;
}

export interface SaveTravelZonePayload {
  name: string;
  defaultTravelHrs: number;
  notes?: string;
}

export interface UpdateTravelZonePayload {
  name?: string;
  defaultTravelHrs?: number;
  notes?: string;
}

export async function fetchSitePlannerTravelZones(
  signal?: AbortSignal,
): Promise<SitePlannerTravelZone[]> {
  const data = await apiFetch<{ zones: SitePlannerTravelZone[] }>(
    '/api/fleet/site-planner/zones',
    { method: 'GET', signal },
  );
  return data.zones;
}

export async function createSitePlannerTravelZone(
  payload: SaveTravelZonePayload,
): Promise<SitePlannerTravelZone> {
  const data = await apiFetch<{ zone: SitePlannerTravelZone }>(
    '/api/fleet/site-planner/zones',
    { method: 'POST', body: payload },
  );
  return data.zone;
}

export async function updateSitePlannerTravelZone(
  id: string,
  patch: UpdateTravelZonePayload,
): Promise<SitePlannerTravelZone> {
  const data = await apiFetch<{ zone: SitePlannerTravelZone }>(
    `/api/fleet/site-planner/zones/${encodeURIComponent(id)}`,
    { method: 'PUT', body: patch },
  );
  return data.zone;
}

export async function deleteSitePlannerTravelZone(id: string): Promise<void> {
  await apiFetch<Record<string, never>>(
    `/api/fleet/site-planner/zones/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

// ─── TACHO Phase B — DDD / VU file upload + history ───────────────────────
//
// Matches the shape returned by listTachoFiles / createTachoFile in
// apps/api/src/services/fleet/tachoFileService.ts. AI-assist fields
// (TachoAiDraft rows) are not exposed yet — they land alongside Phase
// B Step 2 (the inline LLM call) which mirrors the HS RA generator
// pattern (requireAiAssistance gate, direct Anthropic.messages.create,
// writeAuditLog with tacho_file.ai_<kind> action).

export interface TachoFilePublic {
  id: string;
  driverId: string | null;
  driverName: string | null;
  /**
   * TACHO Phase H — 'card' | 'vu' | 'unknown'. Defaults to 'card'
   * for the rows that pre-date the discriminator column.
   */
  fileType: string;
  cardNumber: string | null;
  cardHolderName: string | null;
  /**
   * TODO 080d-5 — how this file got its driver, or why it has none:
   * 'card' | 'name' | 'none' | 'ambiguous' | 'manual'. Null on anything
   * uploaded before the column existed, which is deliberately never
   * backfilled — so the UI has to treat null as "not recorded" rather
   * than as any particular outcome.
   */
  driverMatch: string | null;
  vehicleReg: string | null;
  filename: string;
  sizeBytes: number;
  periodStart: string | null;
  periodEnd: string | null;
  /** 'pending' | 'analysed' | 'partial' | 'failed' */
  parseStatus: string;
  parseError: string | null;
  infringementCount: number;
  advisoryCount: number;
  /**
   * TACHO Phase I — VU-file rollup counters. Always present (default
   * 0). Card files leave these at 0; the modal switches on fileType
   * to decide whether to render the "Vehicle-recorded events" pane.
   */
  vuEventCount: number;
  vuFaultCount: number;
  /**
   * Over-speed events the VU device has counted since the last DVSA
   * over-speed control. NOT a per-driver figure.
   */
  overspeedCount: number;
  /** ISO timestamp of the last DVSA over-speed control, or null. */
  lastOverspeedControlTime: string | null;
  /** 'pending_review' | 'signed_off' | 'returned' | 'escalated' */
  signoffStatus: string;
  signoffById: string | null;
  signoffAt: string | null;
  signoffNote: string | null;
  uploadedAt: string;
  uploadedById: string | null;
}

/**
 * GET /api/fleet/tachograph/files — newest first, optional driverId
 * filter. Returns up to `limit` rows (server clamps to [1, 500]).
 */
export async function fetchTachoFiles(
  opts: { driverId?: string | null; limit?: number } = {},
  signal?: AbortSignal,
): Promise<TachoFilePublic[]> {
  const qs: string[] = [];
  if (opts.driverId) qs.push(`driverId=${encodeURIComponent(opts.driverId)}`);
  if (opts.limit !== undefined) qs.push(`limit=${opts.limit}`);
  const suffix = qs.length > 0 ? `?${qs.join('&')}` : '';
  return apiFetch<TachoFilePublic[]>(`/api/fleet/tachograph/files${suffix}`, {
    method: 'GET',
    signal,
  });
}

// ─── TACHO Phase B Step 2 — AI assist + sign-off ─────────────────────────

export interface TachoAiDraftPublic {
  id: string;
  tachoFileId: string;
  kind: string;
  status: string;
  draftText: string;
  editedText: string | null;
  modelId: string;
  inputTokens: number;
  outputTokens: number;
  createdAt: string;
  reviewedAt: string | null;
}

export async function fetchTachoAiDrafts(
  fileId: string,
  signal?: AbortSignal,
): Promise<TachoAiDraftPublic[]> {
  return apiFetch<TachoAiDraftPublic[]>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/ai-drafts`,
    { method: 'GET', signal },
  );
}

/**
 * Runs the 'interpret' AI assist on a tachograph file. Server gates
 * the call on the tenant's Plus AI add-on (returns 403
 * AI_ASSISTANCE_DISABLED if off). Returns the persisted draft.
 */
export async function generateTachoAiSummary(
  fileId: string,
): Promise<TachoAiDraftPublic> {
  return apiFetch<TachoAiDraftPublic>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/ai-summary`,
    { method: 'POST', body: {} },
  );
}

/**
 * 2026-06-02 — TACHO Phase F. Runs the 'compliance' AI assist. Unlike
 * `generateTachoAiSummary` (which only sees metadata + a string
 * sample), this one feeds the AI the parsed-day rollup + every
 * detected infringement, so it narrates the rule engine's findings
 * instead of guessing. Returns the persisted draft (kind=compliance).
 */
export async function generateTachoComplianceSummary(
  fileId: string,
): Promise<TachoAiDraftPublic> {
  return apiFetch<TachoAiDraftPublic>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/ai-compliance`,
    { method: 'POST', body: {} },
  );
}

export type TachoSignoffDecision = 'accept' | 'edit' | 'return' | 'escalate';

export async function signoffTachoFile(
  fileId: string,
  payload: { decision: TachoSignoffDecision; note?: string; editedText?: string },
): Promise<TachoFilePublic> {
  return apiFetch<TachoFilePublic>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/signoff`,
    { method: 'PUT', body: payload },
  );
}

// ─── TACHO Phase C — per-(driver, week) activity rollup ──────────────────

export interface TachoDayPublic {
  date: string; // YYYY-MM-DD
  startTime: string | null; // HH:MM, null if rest-only / no activity
  driveMinutes: number;
  breakMinutes: number;
  workMinutes: number;
  distanceKm: number | null;
  tachoFileId: string;
}

/**
 * GET /api/fleet/tachograph/days — every TachoDay row in the week
 * window [weekStart, weekStart+6] for the given driver. Empty array
 * when no DDD file has been uploaded for any day in that window.
 */
export async function fetchTachoDaysForDriverWeek(
  driverId: string,
  weekStart: string,
  signal?: AbortSignal,
): Promise<TachoDayPublic[]> {
  const qs = `?driverId=${encodeURIComponent(driverId)}&weekStart=${encodeURIComponent(weekStart)}`;
  return apiFetch<TachoDayPublic[]>(`/api/fleet/tachograph/days${qs}`, {
    method: 'GET',
    signal,
  });
}

// ─── TACHO Phase G — audit trail viewer for one file ─────────────────────

export interface TachoAuditEntryPublic {
  id: string;
  action: string;
  userName: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

/**
 * GET /api/fleet/tachograph/files/:id/audit-trail — every audit_logs
 * row for the file, chronological. Captures upload, parse, rule
 * engine, AI assist calls and manager sign-off decisions.
 */
export async function fetchTachoFileAuditTrail(
  fileId: string,
  signal?: AbortSignal,
): Promise<TachoAuditEntryPublic[]> {
  return apiFetch<TachoAuditEntryPublic[]>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/audit-trail`,
    { method: 'GET', signal },
  );
}

// ─── TACHO Phase E — EU 561/2006 + WTR rule infringements ────────────────

export interface TachoInfringementPublic {
  id: string;
  date: string;
  severity: string;
  ruleKey: string;
  ruleArticle: string;
  detail: string;
  evidence: string;
  eventStartTime: string | null;
  createdAt: string;
}

/**
 * GET /api/fleet/tachograph/files/:id/infringements — every detected
 * breach for the file, chronological. Counts also surface on
 * TachoFilePublic.infringementCount / advisoryCount for the history
 * row's at-a-glance badge.
 */
export async function fetchTachoInfringements(
  fileId: string,
  signal?: AbortSignal,
): Promise<TachoInfringementPublic[]> {
  return apiFetch<TachoInfringementPublic[]>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/infringements`,
    { method: 'GET', signal },
  );
}

// ─── TACHO Phase I — Vehicle-recorded events (VU files only) ─────────────

export type TachoVuCardSlotState =
  | 'driver_card_in'
  | 'co_driver_card_in'
  | 'both_cards_in'
  | 'no_card'
  | 'unknown';

export type TachoVuEventCategory =
  | 'driver'
  | 'vehicle'
  | 'security'
  | 'unknown';

export interface TachoVuEventPublic {
  id: string;
  /** 'event' (VuEventRecord) or 'fault' (VuFaultRecord). */
  recordKind: 'event' | 'fault';
  /**
   * Numeric / hex code from the regulation (e.g. "4" for general
   * events, "5" for vehicle-motion conflict). Null when the file
   * carried only a label.
   */
  eventCode: string | null;
  /** Human-readable event name, e.g. "Driving without an appropriate card". */
  eventLabel: string;
  /** "Last 10 most recent" / "longest" / etc., or null. */
  recordPurpose: string | null;
  eventStart: string | null;
  eventEnd: string | null;
  durationSeconds: number | null;
  // 2026-06-03 Phase I+ — driver attribution + classification.
  /** Card in the driver slot at event start (e.g. "UK1234567890"). */
  driverCardNumber: string | null;
  /** Card in the co-driver slot at event start. */
  coDriverCardNumber: string | null;
  /** Slot state at event start. 'no_card' is asserted for code 4
   *  ("Driving without an appropriate card") even when the file
   *  didn't carry the slot fields. */
  cardSlotState: TachoVuCardSlotState | null;
  /** Drives the row's visual style:
   *  driver — card / behaviour, follow-up. vehicle — power / sensor
   *  fault, workshop. security — tamper, DVSA-grade. */
  eventCategory: TachoVuEventCategory;
  /** Driver row matched on `driverCardNumber` (or co-driver as
   *  fallback). Null when no Driver in this tenant carries that
   *  card number, or the slot was empty. */
  driverId: string | null;
  driverName: string | null;
  /** 2026-06-03 Phase I+ chunk 1.5 — DriverCardHistory resolution
   *  status for driverCardNumber. UI uses this to pick attribution
   *  copy ("Driver: …", "Driver: … (historical card)", "Card …
   *  acknowledged", "Card … — not assigned [Assign]"). */
  driverCardLinkStatus:
    | 'current'
    | 'historical'
    | 'acknowledged_unowned'
    | 'unknown'
    | null;
  // 2026-06-03 Phase I+ chunk 1 — card-timeline inference for events
  // with no card present at event time. UI uses the inferenceSource
  // to phrase attribution differently ("Probably …" vs "Driver …").
  /** Card we inferred this event belonged to, from the file's own
   *  code-5 "Card insertion while driving" events. Null for direct
   *  events (the card is in driverCardNumber) and for 'none'. */
  inferredDriverCardNumber: string | null;
  /** Provenance of attribution. `direct` = card was in slot;
   *  `card_insertion_*` = chunk 1 (file's own timeline);
   *  `hgv_schedule*` = chunk 2 (HGV Planner cross-ref);
   *  `none` = nothing available. UI phrases each differently. */
  inferenceSource:
    | 'direct'
    | 'card_insertion_both'
    | 'card_insertion_before'
    | 'card_insertion_after'
    | 'hgv_schedule'
    | 'hgv_schedule_multiple'
    | 'none'
    | null;
  /** ISO timestamp of the source code-5 event we inferred from. */
  inferenceEvidenceAt: string | null;
  /** Driver row matched on `inferredDriverCardNumber`. */
  inferredDriverId: string | null;
  inferredDriverName: string | null;
  /** Link status for inferredDriverCardNumber. */
  inferredCardLinkStatus:
    | 'current'
    | 'historical'
    | 'acknowledged_unowned'
    | 'unknown'
    | null;
}

/** 2026-06-03 Phase I+ chunk 1.5 — DriverCardHistory link payloads. */
export interface DriverCardLinkResult {
  cardNumber: string;
  status: 'current' | 'historical' | 'acknowledged_unowned';
  driverId: string | null;
  driverName: string | null;
  notes: string | null;
}

/**
 * POST /api/fleet/driver-cards/link — assign a tachograph card
 * number to an existing Driver row. Used by the modal's [Assign]
 * picker on events whose card number isn't yet on the Driver
 * Register. Manager / director only.
 */
export async function linkDriverCard(payload: {
  cardNumber: string;
  driverId: string;
  notes?: string | null;
}): Promise<DriverCardLinkResult> {
  return apiFetch<DriverCardLinkResult>(`/api/fleet/driver-cards/link`, {
    method: 'POST',
    body: payload,
  });
}

/**
 * POST /api/fleet/driver-cards/acknowledge-unowned — mark a card as
 * belonging to someone not on the platform (former employee,
 * sub-contractor, etc.). Stops the modal flagging it as a data gap.
 * Manager / director only.
 */
export async function acknowledgeUnownedDriverCard(payload: {
  cardNumber: string;
  notes?: string | null;
}): Promise<DriverCardLinkResult> {
  return apiFetch<DriverCardLinkResult>(
    `/api/fleet/driver-cards/acknowledge-unowned`,
    { method: 'POST', body: payload },
  );
}

/**
 * GET /api/fleet/tachograph/files/:id/vu-events — vehicle-recorded
 * events + faults parsed from a VU file. Always empty for card files
 * (the table is VU-only). Newest event first.
 */
export async function fetchTachoVuEvents(
  fileId: string,
  signal?: AbortSignal,
): Promise<TachoVuEventPublic[]> {
  return apiFetch<TachoVuEventPublic[]>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/vu-events`,
    { method: 'GET', signal },
  );
}

/**
 * POST /api/fleet/tachograph/files/:id/reparse-vu — re-runs the VU
 * events parser against the stored file blob and overwrites both the
 * tacho_vu_events rows + the 4 rollup columns on the file row.
 * Idempotent. Director/manager only. Returns the refreshed file row
 * so the caller can update its history list in place.
 */
export async function reparseTachoVuFile(
  fileId: string,
): Promise<TachoFilePublic> {
  return apiFetch<TachoFilePublic>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/reparse-vu`,
    { method: 'POST', body: {} },
  );
}

// ─── TACHO Phase D — Driver Reported from Rota clock-ins ─────────────────

export interface DriverReportedDayPublic {
  date: string;
  startTime: string | null;
  driveMinutes: number;
  breakMinutes: number;
  /** Total duty time (drive + break + on-site + other-work for
   *  tablet-sourced rows, or total clock-in duration for kiosk
   *  fallback). */
  workMinutes: number;
  /** Tablet T3 phase 1 — on-site portion of the day (delivery /
   *  yard / office) when a tablet session exists. */
  onSiteMinutes: number;
  /** Tablet T3 phase 1 — duty time that isn't drive / break /
   *  on-site (e.g. gate, walkaround, ready, fuelling). */
  otherWorkMinutes: number;
  /** Provenance of the row: 'tablet' (completed session),
   *  'tablet_live' (in-progress activity events), 'kiosk' (ClockIn
   *  only), 'none' (rest day). */
  source: 'tablet' | 'tablet_live' | 'kiosk' | 'none';
  /** 2026-06-04 — true when this day still has an open ClockIn.
   *  Comparison grid shows "🚛 In progress" instead of "All match"
   *  / "Discrepancy" since totals can't be expected to match a
   *  full-day schedule mid-shift. */
  isInProgress: boolean;
  /** 2026-06-04 — compliance breaches detected automatically from
   *  the live tablet activity stream. UI surfaces these as red
   *  strips under the Driver Reported column + a "⛔ X
   *  infringements" badge in the day-card header. */
  infringements: Array<{
    ruleCode: string;
    severity: 'critical' | 'warning';
    message: string;
    occurredAt: string;
  }>;
  /** 2026-06-04 FT-DTAB-PERIODS-01 — Period 1/2/3 split for this
   *  day (same shape and source as the Live Drivers feed). Empty
   *  array for source='tablet'/'kiosk'/'none'; populated for
   *  source='tablet_live'. */
  periods: Array<{
    index: 1 | 2 | 3;
    startedAt: string | null;
    endedAt: string | null;
    driveSeconds: number;
    breakSeconds: number;
    onSiteSeconds: number;
    resetKind: '45_single' | '15+30_split' | 'rest' | 'none_yet';
  }>;
}

/**
 * GET /api/fleet/tachograph/driver-reported — 7-day rollup of
 * ClockIn events for the driver's linked Employee. Clock-ins don't
 * tell us Drive vs Break vs On-site, so driveMinutes and
 * breakMinutes come back as 0; workMinutes is total clocked-in time.
 * Driver tablet surface (when built) will replace this with proper
 * state-transition data and the comparison grid renders the same way.
 */
export async function fetchDriverReportedWeek(
  driverId: string,
  weekStart: string,
  signal?: AbortSignal,
): Promise<DriverReportedDayPublic[]> {
  const qs = `?driverId=${encodeURIComponent(driverId)}&weekStart=${encodeURIComponent(weekStart)}`;
  return apiFetch<DriverReportedDayPublic[]>(
    `/api/fleet/tachograph/driver-reported${qs}`,
    { method: 'GET', signal },
  );
}

/** Part 4/2 Half 2 — per-driver rolling WTR Working Time (duty-span basis)
 *  over the operator-elected reference period. Backs the Tachograph tab's
 *  17/26-week reference tile. */
export interface DriverWorkingTimePublic {
  rollingWorkingTimeSeconds: number;
  wtrReferencePeriod: 'rolling_17' | 'rolling_26';
  wtrReferenceCapSeconds: number;
}

export async function fetchDriverWorkingTime(
  driverId: string,
  weekStart: string,
  signal?: AbortSignal,
): Promise<DriverWorkingTimePublic> {
  const qs = `?driverId=${encodeURIComponent(driverId)}&weekStart=${encodeURIComponent(weekStart)}`;
  return apiFetch<DriverWorkingTimePublic>(
    `/api/fleet/tachograph/driver-working-time${qs}`,
    { method: 'GET', signal },
  );
}

/**
 * 2026-06-03 — soft-delete a tachograph file from the history. Server
 * sets deletedAt + writes the audit row; tacho_days / tacho_infringements /
 * tacho_ai_drafts stay in place so the row can be restored later if
 * needed (nothing hard-deletes tachograph data; TODO 542).
 */
export async function deleteTachoFile(fileId: string): Promise<void> {
  await apiFetch<unknown>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}`,
    { method: 'DELETE' },
  );
}

/** A driver who could own an unattributed file — TODO 080d-5. */
export interface TachoDriverCandidate {
  driverId: string;
  employeeId: string;
  name: string;
}

export interface TachoDriverCandidates {
  /** The card holder as the parser read it out of the .ddd. */
  cardHolderName: string | null;
  /**
   * Drivers whose name matches — for an ambiguous file, exactly the ones
   * the platform refused to choose between.
   */
  plausible: TachoDriverCandidate[];
  /**
   * Every driver. Offered as a second step rather than the default,
   * because "plausible only" has a dead end in it: when nobody matches
   * the name there would be nothing to pick, and that is the commonest
   * real case — a married name, a misspelling on the card.
   */
  all: TachoDriverCandidate[];
}

/** A file the OLD matcher could have got wrong — TODO 080d-5 follow-up. */
export interface SuspectAttribution {
  tachoFileId: string;
  filename: string;
  uploadedAtIso: string;
  cardHolderName: string | null;
  cardNumber: string | null;
  attributedDriverId: string;
  attributedDriverName: string | null;
  /** The drivers it was picked from — all sharing that surname. */
  candidates: TachoDriverCandidate[];
}

/**
 * Read-only. Director or transport manager only; 403 for anybody else,
 * which the caller should render as "not for you" rather than as empty.
 */
export async function fetchSuspectAttributions(
  signal?: AbortSignal,
): Promise<SuspectAttribution[]> {
  return apiFetch<SuspectAttribution[]>(
    `/api/fleet/tachograph/attribution-review`,
    { method: 'GET', signal },
  );
}

export async function fetchTachoDriverCandidates(
  fileId: string,
  signal?: AbortSignal,
): Promise<TachoDriverCandidates> {
  return apiFetch<TachoDriverCandidates>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/driver-candidates`,
    { method: 'GET', signal },
  );
}

export interface TachoReattributeResult {
  tachoFileId: string;
  driverId: string | null;
  driverName: string | null;
  daysMoved: number;
  infringementsMoved: number;
  wasSignedOff: boolean;
}

/**
 * Attribute a file to a driver, or to nobody — TODO 080d-5.
 *
 * Moves the file, its parsed days AND its infringements together; the
 * counts come back so the screen can say what actually moved. Director or
 * Transport Manager only.
 */
export async function assignTachoFileDriver(
  fileId: string,
  driverId: string | null,
): Promise<TachoReattributeResult> {
  return apiFetch<TachoReattributeResult>(
    `/api/fleet/tachograph/files/${encodeURIComponent(fileId)}/driver`,
    { method: 'POST', body: { driverId } },
  );
}

/**
 * POST /api/fleet/tachograph/files (multipart-form).
 *
 * apiFetch only handles JSON, so we build FormData and run fetch
 * directly — same pattern as uploadAttachment in attachmentsApi.ts.
 */
export async function uploadTachoFile(file: File): Promise<TachoFilePublic> {
  const fd = new FormData();
  fd.append('file', file);

  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(apiUrl('/api/fleet/tachograph/files'), {
    method: 'POST',
    headers,
    body: fd,
  });

  let envelope: unknown = null;
  try {
    envelope = await res.json();
  } catch {
    throw new Error('Upload failed');
  }

  if (
    typeof envelope === 'object' &&
    envelope !== null &&
    'success' in envelope &&
    (envelope as { success: boolean }).success === true &&
    'data' in envelope
  ) {
    return (envelope as { data: TachoFilePublic }).data;
  }
  if (
    typeof envelope === 'object' &&
    envelope !== null &&
    'error' in envelope
  ) {
    const err = (envelope as { error: { code: string; message: string } }).error;
    throw new Error(err.message || err.code || 'Upload failed');
  }
  throw new Error('Upload failed');
}

/*
 * Fleet toolbox talks the tenant can take — FLEET-TOOLBOX-NOT-ON-THE-REGISTER-001,
 * 2026-09-01. Authored in JeanieIQ Admin -> Country Configuration -> Fleet
 * Toolbox; offered by the Document Register's "+ Add document" picker so a talk
 * given to drivers carries a DOC number like every other controlled document.
 */
export interface AvailableFleetTalk {
  id: string;
  title: string;
  topic: string | null;
  /** The talk's filing category: a key on the country's category list. */
  contentCategory: string;
  /** Its name on that list - what a person reads. */
  contentCategoryLabel: string;
  preview: string;
  /** The company used and archived it; adding it reinstates its own copy (2026-10-14). */
  usedAndArchived?: boolean;
}

export async function fetchAvailableFleetToolboxTalks(
  signal?: AbortSignal,
): Promise<AvailableFleetTalk[]> {
  const data = await apiFetch<{ documents: AvailableFleetTalk[] }>(
    '/api/fleet/toolbox/available',
    { method: 'GET', signal },
  );
  return data.documents;
}

export async function adoptFleetToolboxTalk(
  id: string,
): Promise<{ id: string; title: string; documentNumber: string | null; reinstated: boolean }> {
  return apiFetch<{ id: string; title: string; documentNumber: string | null; reinstated: boolean }>(
    `/api/fleet/toolbox/available/${encodeURIComponent(id)}/adopt`,
    { method: 'POST' },
  );
}
